/**
 * BlueView's map engine — an OpenLayers port of what was a Leaflet
 * component (MapView.tsx, deleted in Phase 14b once this became the sole,
 * default implementation; see the migration plan for the phase history).
 *
 * THROUGH PHASE 13: basemap gallery, controlled center/zoom/bounds view
 * contract, container-resize handling, ArcGIS FeatureServer + local vector
 * rendering (single-symbol + unique-value, lines/polygons/points, points
 * including the full symbol gallery), GeoServer WMS tile layers (server-side
 * SLD styling + a separate label request), ArcGIS dynamic/export (MapServer)
 * layers via OL's native TileArcGISRest, local raster/GeoTIFF layers (both
 * the lightweight pre-flattened-image path and the legacy raw-GeoTIFF
 * decode path via OL's native ol/source/GeoTIFF), the Density tool's
 * heatmap output via OL's native Heatmap layer replacing leaflet.heat,
 * point clustering (NEW — off by default per layer, opted into from the
 * Point symbology panel's own toggle), the Draw/Measure/Edit sketch
 * interactions (ol/interaction/Draw + Translate replacing MapView.tsx's own
 * hand-rolled click/mousemove/dblclick state machine and drag-handle code),
 * finished measurements, tool overlays, the attribute-table selection
 * highlight, and the Swipe tool (OL's prerender/postrender canvas-clip
 * cookbook pattern replacing Leaflet's pane + CSS clip-path trick), the
 * click-identify pop-up (GeoServer WMS GetFeatureInfo, ArcGIS REST query,
 * and client-side hit-testing for imported layers, all funnelled through
 * the same FeatureAttributePopup card and per-layer popupConfigs used by
 * MapView.tsx — see queryGeoServerWmsOL/queryEsriRestOL/hitTestFeaturesOL),
 * the plain onMapPoint click reporter, the live scale readout, the
 * search-result marker, and the client-side label overlay (labelLayers —
 * one canvas repainted on every 'postrender', replacing LabelOverlay.tsx's
 * whole Leaflet-pane workaround, see that effect's own doc comment). Every
 * prop MapView.tsx accepts is now wired up — this component is a full,
 * drop-in replacement for MapView.tsx at every call site.
 *
 * Leaflet's `[lat, lng]` convention is kept at this component's OWN prop
 * boundary (so callers never change) and converted at the edge: OpenLayers'
 * default view projection is Web Mercator (EPSG:3857), so every prop-in
 * uses `fromLonLat([lng, lat])` and every event-out uses `toLonLat(...)`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Map from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import ClusterSource from 'ol/source/Cluster';
import TileWMS from 'ol/source/TileWMS';
import TileArcGISRest from 'ol/source/TileArcGISRest';
import ImageLayer from 'ol/layer/Image';
import ImageStatic from 'ol/source/ImageStatic';
import WebGLTileLayer from 'ol/layer/WebGLTile';
import GeoTIFFSource from 'ol/source/GeoTIFF';
import HeatmapLayer from 'ol/layer/Heatmap';
import GeoJSONFormat from 'ol/format/GeoJSON';
import { Style, Stroke, Fill, Circle as CircleStyle, Icon, Text } from 'ol/style';
import type { FeatureLike } from 'ol/Feature';
import OLFeature from 'ol/Feature';
import Point from 'ol/geom/Point';
import MultiPoint from 'ol/geom/MultiPoint';
import Translate from 'ol/interaction/Translate';
import Draw, { createBox } from 'ol/interaction/Draw';
import Snap from 'ol/interaction/Snap';
import type { default as DrawInteraction } from 'ol/interaction/Draw';
import type BaseLayer from 'ol/layer/Base';
import type RenderEvent from 'ol/render/Event';
import DragPan from 'ol/interaction/DragPan';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj';
import { createEmpty, extend, getCenter, isEmpty } from 'ol/extent';
import type { Extent } from 'ol/extent';
import 'ol/ol.css';
import {
  MAX_INTERACTIVE_ZOOM,
  type ArcgisLayerStyle,
  type SwipeTarget,
  type WmsLayerStyle,
  type LayerRenderer,
  type LayerGeometryType,
} from './types';
import { scaleAt, zoomForScale, parseScale, isWithinScale } from './labeling/ScaleVisibility';
import { labelingIsActive, type LayerLabeling, type LabelLayerInput } from './labeling/LabelTypes';
import { passesLabelFilter, resolveLabelText } from './labeling/LabelExpression';
import { anchorsForGeometry, type Project } from './labeling/LabelUtils';
import { drawLabels, type LabelDrawItem } from './labeling/LabelRenderer';
import FeatureAttributePopup from './components/FeatureAttributePopup';
import {
  extractMetadata,
  formatFeatureForPopup,
  type FeatureInfoResult,
  type MetadataEntry,
} from './utils/featureFormat';
import {
  queryEsriRestOL,
  queryGeoServerWmsOL,
  type FeatureQueryResult,
  type ServiceCapabilities,
} from './services/featureInfoService';
import { resolvePopupFields, resolvePopupTitle } from './popup/PopupRenderer';
import {
  arcgisPopupKey,
  localPopupKey,
  normalisePopup,
  wmsPopupKey,
  type LayerPopup,
} from './popup/PopupTypes';
import { hitTestFeaturesOL } from './layers/localHitTest';
import { DEFAULT_RENDERER, getLineDashArray, buildSymbolSvg, getPointSymbol, mergeCategoryStyle } from './layers/symbols';
import { getArcgisBaseUrl, getArcgisServiceKind, getArcgisToken, withArcgisToken } from './utils/arcgis';
import { buildSld, buildLabelSld, isSldTooLarge } from './layers/sld';

/** A formatted feature plus the footer metadata and capability flags — same
 *  shape as MapView.tsx's own (unexported) local type of the same name. */
type PopupFeature = FeatureInfoResult & {
  metadata: MetadataEntry[];
  capabilities: ServiceCapabilities;
  displayTitle: string;
  geometry?: GeoJSON.Geometry;
};

/** An imported layer has no service, so none of the service-backed actions
 *  on the pop-up card apply to it — mirrors MapView.tsx's own constant. */
const LOCAL_CAPABILITIES: ServiceCapabilities = {
  edit: false,
  related: false,
  attachments: false,
  attachmentCount: 0,
};

const SPINNER_DELAY_MS = 350;

// Mirrors MapView.tsx's own local (not exported) `Basemap` type — unify the
// two once the old MapView.tsx is deleted in the migration's cleanup phase.
type Basemap = 'street' | 'terrain' | 'satellite' | 'dark';

// Same four sources as MapView.tsx. Leaflet's `{s}` subdomain-rotation token
// has no OL equivalent on a plain `url` (OL round-robins via a `urls` array
// instead) — pinned to a single subdomain here rather than adding that
// complexity for four low-traffic public tile sources.
export const BASEMAPS: Record<Basemap, { url: string; attribution: string; maxNativeZoom: number }> = {
  street: {
    url: 'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
    maxNativeZoom: 19,
  },
  terrain: {
    url: 'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors, SRTM | © OpenTopoMap',
    maxNativeZoom: 17,
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles © Esri',
    maxNativeZoom: 19,
  },
  dark: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles © Esri',
    maxNativeZoom: 16,
  },
};

/**
 * Layer stacking order, as explicit OL z-index values — replacing Leaflet's
 * PANE hierarchy, which is what actually decided this in MapView.tsx: tile
 * layers (basemap, WMS) live in Leaflet's `tilePane`, vector overlays
 * (ArcGIS FeatureServer, local GeoJSON) live in the separate, always-higher
 * `overlayPane`, so vectors render above WMS regardless of either side's
 * own numeric z-index. OL has no panes — everything is one ordered layer
 * list — so that same relationship has to be made explicit here instead.
 */
const BASEMAP_ZINDEX = 0;
const WMS_DATA_ZINDEX = 10;
const WMS_LABEL_ZINDEX = 11;
const ARCGIS_DYNAMIC_ZINDEX = 15;
const VECTOR_ZINDEX = 20;

/**
 * Where a local layer's z-index lands within its tier, from its position in
 * the Layers tab — the TOC's top row (index 0) gets the HIGHEST z-index in
 * the tier, so it draws over every row below it, matching "Layer order
 * drives draw order on the map" (App.tsx's own `moveLayer` comment). Without
 * this, local vector/raster/heatmap layers all shared one flat `tierBase`
 * and stacked by whichever of several independent effects happened to call
 * `map.addLayer` last — insertion order, not the order shown in the TOC.
 */
function tocZIndex(layerId: string | undefined, order: string[], tierBase: number): number {
  const index = layerId ? order.indexOf(layerId) : -1;
  if (index === -1) return tierBase;
  return tierBase + (order.length - 1 - index);
}

/**
 * The map scale "zoom to selected" never zooms in closer than — every
 * "zoom to" in this app (the attribute table, Select, Query, Near Me,
 * finishing a Draw shape) shares this one predictable scale for anything
 * small enough to fit inside it, so a single point always lands somewhere
 * familiar. Anything bigger than this scale can show (a long line, a large
 * polygon) still zooms OUT further than this — see the "fit to selection"
 * effect below, which only ever uses this as a maximum, never a fixed
 * value, specifically so a feature is never cropped to force this scale.
 */
const ZOOM_TO_SELECTION_SCALE = 1000;

/** Same normalisation as MapView.tsx's own (unexported) helper of the same
 *  name — ensures the URL ends in `/wms` and carries no query string. */
function getWmsBaseUrl(geoServerUrl: string): string {
  try {
    const url = new URL(geoServerUrl.trim());
    const path = url.pathname.replace(/\/+$/, '');
    url.pathname = /\/wms$/i.test(path) ? path : `${path}/wms`;
    url.search = '';
    return url.toString();
  } catch {
    return '';
  }
}

/** Ported verbatim from MapView.tsx (pure JS, no Leaflet dependency) —
 *  reverses geotiff.ts's `arrayBufferToBase64`; the stored copy round-trips
 *  losslessly since both ends work in raw bytes, never text. */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes.buffer;
}

/** Great-circle distance between two [lng, lat] points, in metres — ported
 *  verbatim from MapView.tsx's own `groundDistance` (pure haversine maths,
 *  no Leaflet dependency beyond the L.LatLng parameter type there, swapped
 *  for a plain tuple here). */
function groundDistance(a: number[], b: number[]): number {
  const R = 6371008.8;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** A label's rotation so it reads along its segment on screen — computed
 *  from the segment's own projected (EPSG:3857) coordinates, which matches
 *  screen angle exactly for an unrotated view (this app has no map-rotate
 *  control). Flipped past 90° so text never prints upside down. */
function labelRotationRad(from: number[], to: number[]): number {
  let angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
  const HALF_PI = Math.PI / 2;
  if (angle > HALF_PI) angle -= Math.PI;
  if (angle < -HALF_PI) angle += Math.PI;
  return -angle; // OL's Text rotation is clockwise-positive; screen atan2 is counter-clockwise.
}

/** One measurement/total label, as an OL Style with a Point geometry at
 *  the given projected coordinate — the same "a Style can carry its own
 *  geometry, independent of the feature's" trick every per-segment label
 *  below relies on, since one feature needs several labels at once. */
function measureLabelStyle(coord: number[], text: string, rotation: number, total = false): Style {
  return new Style({
    geometry: new Point(coord),
    text: new Text({
      text,
      font: total ? '700 12px sans-serif, system-ui' : '600 11px sans-serif, system-ui',
      rotation,
      fill: new Fill({ color: '#0F2942' }),
      stroke: new Stroke({ color: '#ffffff', width: 3 }),
      offsetY: total ? -14 : -8,
    }),
  });
}

/**
 * Phase 10 — the Swipe tool. Replaces MapView.tsx's pane + CSS `clip-path`
 * trick (a Leaflet tile/vector layer is "a bag of positioned tiles with no
 * single box to clip," so the whole PANE was clipped instead — see that
 * file's own SwipeDivider comment) with OL's documented cookbook pattern
 * for exactly this: clip the swiped layer's own canvas on `prerender`,
 * restore it on `postrender`. Only the ONE layer identified by `swipe`
 * (never the basemap or anything else) gets these listeners.
 *
 * Reads the live position from a ref rather than a closed-over value, so
 * dragging the divider (a plain React state update, not a view change)
 * doesn't need to tear down and reattach the listeners on every pixel of
 * movement — same reasoning FeatureReshapeController's own doc comment
 * gives for staying imperative during a drag.
 */
interface SwipeClipState {
  vertical: boolean;
  /** 0-1 along the axis — the swiped layer is visible from the start up to
   *  this fraction, matching MapView.tsx's own inset() maths exactly. */
  position: number;
}
function attachSwipeClip(layer: BaseLayer, stateRef: { current: SwipeClipState | null }): () => void {
  const prerender = (event: RenderEvent) => {
    const state = stateRef.current;
    const ctx = event.context as CanvasRenderingContext2D | undefined;
    const frameState = event.frameState;
    if (!state || !ctx || !frameState) return;
    const [width, height] = frameState.size;
    const ratio = frameState.pixelRatio || 1;
    ctx.save();
    ctx.beginPath();
    if (state.vertical) {
      ctx.rect(0, 0, width * ratio, height * ratio * state.position);
    } else {
      ctx.rect(0, 0, width * ratio * state.position, height * ratio);
    }
    ctx.clip();
  };
  const postrender = (event: RenderEvent) => {
    if (!stateRef.current) return;
    (event.context as CanvasRenderingContext2D | undefined)?.restore();
  };
  // `prerender`/`postrender` are generic render events every OL layer type
  // actually fires, but OL's TS defs type `.on`/`.un` per concrete layer
  // subclass's own narrower event map, which doesn't include them at the
  // shared `BaseLayer` level this helper is written against — an `as any`
  // pair here, not a real type mismatch, since this is the documented OL
  // cookbook API for exactly this use.
  const anyLayer = layer as unknown as { on: (t: string, l: (e: RenderEvent) => void) => void; un: (t: string, l: (e: RenderEvent) => void) => void };
  anyLayer.on('prerender', prerender);
  anyLayer.on('postrender', postrender);
  return () => {
    anyLayer.un('prerender', prerender);
    anyLayer.un('postrender', postrender);
  };
}

/* ── Shared vector styling — ported from MapView.tsx's identically-named
   functions verbatim (resolveFeatureColor / dash lookup are pure JS with no
   Leaflet dependency), rendered here through an OL style function instead
   of Leaflet's split style/pointToLayer callbacks. ────────────────────── */

const FALLBACK_RENDERER: LayerRenderer = DEFAULT_RENDERER;

/** The unique-value category (if any) a feature's field value matches. */
function matchedCategory(renderer: LayerRenderer, feature: any) {
  if (renderer.type !== 'unique' || !renderer.field) return undefined;

  const properties = feature?.properties ?? {};
  let value = properties[renderer.field];
  if (value === undefined) {
    const key = Object.keys(properties).find(
      (candidate) => candidate.toLowerCase() === renderer.field!.toLowerCase(),
    );
    if (key) value = properties[key];
  }
  if (value === null || value === undefined) return undefined;

  const asString = String(value).trim();
  return renderer.categories?.find((category) => category.value.trim() === asString);
}

export function resolveFeatureColor(renderer: LayerRenderer, feature: any): string {
  if (renderer.type !== 'unique' || !renderer.field) return renderer.color;
  return matchedCategory(renderer, feature)?.color ?? renderer.defaultColor ?? renderer.color;
}

/**
 * The renderer a feature should actually draw with. For a unique-value
 * renderer, the matched category's own symbol overrides (see
 * RendererCategory — a different point symbol, line style, fill pattern,
 * etc. per feature type) are layered on top of the renderer's global
 * values, so a category that never set its own symbol still renders exactly
 * like every other one (today's behaviour, unchanged). A single-symbol
 * renderer is returned as-is.
 */
function effectiveRendererFor(renderer: LayerRenderer, feature: any): LayerRenderer {
  if (renderer.type !== 'unique' || !renderer.field) return renderer;
  const match = matchedCategory(renderer, feature);
  if (!match) return { ...renderer, color: renderer.defaultColor ?? renderer.color };
  return mergeCategoryStyle(renderer, match);
}

function hasDrawableGeometry(feature: any): boolean {
  const coordinates = feature?.geometry?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length === 0) return false;
  const firstFiniteDepth = (value: any): boolean =>
    Array.isArray(value) ? value.length > 0 && firstFiniteDepth(value[0]) : Number.isFinite(value);
  return firstFiniteDepth(coordinates);
}

/** `#rrggbb` (+ an 0-1 opacity) -> `rgba(r,g,b,a)`. OL's Stroke/Fill colour
 *  option carries its own alpha rather than a separate opacity property, the
 *  one real API-shape difference from Leaflet's style object here. */
function hexToRgb(hexColor: string): [number, number, number] {
  const hex = (hexColor || '#000000').replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex.padEnd(6, '0');
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  return [r, g, b];
}

function withAlpha(hexColor: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hexColor);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
}

/**
 * BlueView's heatmap gradient is arbitrary, non-evenly-spaced `{stop,
 * color}` pairs (see HeatmapConfig's own doc comment) — the same shape a
 * canvas `createLinearGradient` + `addColorStop` takes, which is how
 * `leaflet.heat` reads it. OL's Heatmap layer instead takes a plain
 * `string[]` of colours, sampled evenly across 0-1 — so passing the
 * configured colours straight through in order would silently compress or
 * stretch the ramp away from where each stop was actually set. This
 * resamples the real, positioned gradient into N evenly-spaced colours
 * first, so OL's simpler input still reproduces the intended ramp.
 * Positions outside the first/last configured stop clamp to that stop's
 * colour, matching a canvas gradient's own behaviour at its edges.
 */
function sampleGradient(stops: { stop: number; color: string }[], count = 32): string[] {
  if (stops.length === 0) return ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff0000'];
  const sorted = [...stops].sort((a, b) => a.stop - b.stop);
  const samples: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const t = i / (count - 1);
    if (t <= sorted[0].stop) {
      samples.push(sorted[0].color);
      continue;
    }
    if (t >= sorted[sorted.length - 1].stop) {
      samples.push(sorted[sorted.length - 1].color);
      continue;
    }
    const upperIndex = sorted.findIndex((entry) => entry.stop >= t);
    const lower = sorted[upperIndex - 1];
    const upper = sorted[upperIndex];
    const span = upper.stop - lower.stop || 1;
    const localT = (t - lower.stop) / span;
    const [r1, g1, b1] = hexToRgb(lower.color);
    const [r2, g2, b2] = hexToRgb(upper.color);
    const r = Math.round(r1 + (r2 - r1) * localT);
    const g = Math.round(g1 + (g2 - g1) * localT);
    const b = Math.round(b1 + (b2 - b1) * localT);
    samples.push(`rgb(${r}, ${g}, ${b})`);
  }
  return samples;
}

/** `"8,6"` (MapView's Leaflet-SVG dash string format) -> `[8, 6]` (OL's
 *  `lineDash` wants a number array, not a string). */
function parseDashArray(dash: string | undefined): number[] | undefined {
  if (!dash) return undefined;
  const values = dash.split(',').map((part) => Number(part.trim())).filter((n) => Number.isFinite(n));
  return values.length > 0 ? values : undefined;
}

/**
 * One OL style function per renderer, standing in for MapView's separate
 * `vectorStyleFor`/`pointLayerFor` pair — OL has a single per-feature style
 * callback for every geometry type, so both are folded into one function
 * that branches on `feature.getGeometry().getType()`.
 *
 * Points render as a plain circle for now (Phase 3 adds the symbol gallery
 * — divIcon's OL equivalent — on top of this).
 */
/**
 * Point symbols reuse `layers/symbols.ts`'s own SVG builder — the same
 * function the symbol gallery preview and the Leaflet divIcon both already
 * use, so a chosen symbol looks pixel-identical to its gallery thumbnail
 * and legend entry regardless of which map engine drew it. The SVG is
 * wrapped as a data-URI `ol/style/Icon`, OL's equivalent of a divIcon.
 * (The alternative — rebuilding every gallery shape as a native OL
 * RegularShape/Circle primitive — was considered and set aside for this
 * phase: it would require re-deriving each shape's own path geometry a
 * second time and risks drifting from the gallery/legend previews, which
 * still render through this same SVG builder.)
 *
 * Cached by every value that changes the rendered pixels, since constructing
 * a fresh `Icon` (which parses/decodes the data-URI as an image) on every
 * style-function call is real, avoidable work for a layer with many points.
 */
// `globalThis.Map`, not the bare `Map` identifier: this file imports OL's
// own (non-generic) `Map` class as its default export for the map instance
// itself, which shadows the built-in generic `Map` everywhere below it.
const iconStyleCache = new globalThis.Map<string, Style>();
function pointSymbolStyle(renderer: LayerRenderer, color: string, opacity: number): Style {
  const key = [
    renderer.symbolId, renderer.pointSize, color, renderer.outlineColor,
    renderer.outlineWidth, renderer.fillOpacity, renderer.rotation ?? 0, opacity,
  ].join('|');
  const cached = iconStyleCache.get(key);
  if (cached) return cached;

  const svg = buildSymbolSvg({
    path: getPointSymbol(renderer.symbolId!).path,
    size: renderer.pointSize,
    color,
    outlineColor: renderer.outlineColor,
    outlineWidth: renderer.outlineWidth,
    opacity: renderer.fillOpacity * opacity,
    rotation: renderer.rotation ?? 0,
  });
  const style = new Style({
    image: new Icon({
      src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
      // The SVG already bakes its own rotation into inline CSS (see
      // buildSymbolSvg), so this anchors it without a second, doubled
      // rotation from OL's own `rotation` option.
      anchor: [0.5, 0.5],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
    }),
  });
  iconStyleCache.set(key, style);
  return style;
}

export function styleFunctionFor(renderer: LayerRenderer, opacity: number) {
  return (feature: FeatureLike): Style => {
    const geomType = feature.getGeometry()?.getType() ?? '';
    const properties = (feature.getProperties?.() ?? {}) as Record<string, unknown>;
    // Per-category symbol overrides (a different point symbol/line style/
    // fill pattern per feature type, not just a different colour) — see
    // RendererCategory and mergeCategoryStyle.
    const effective = effectiveRendererFor(renderer, { properties });
    const color = effective.color;
    const isLine = geomType === 'LineString' || geomType === 'MultiLineString';
    const isPoint = geomType === 'Point' || geomType === 'MultiPoint';

    if (isPoint) {
      // A chosen gallery symbol renders as an SVG icon; without one, the
      // original lightweight circle marker (unchanged from Phase 2).
      if (effective.symbolId && effective.symbolId !== 'circle') {
        return pointSymbolStyle(effective, color, opacity);
      }
      return new Style({
        image: new CircleStyle({
          radius: Math.max(2, effective.pointSize / 2),
          fill: new Fill({ color: withAlpha(color, effective.fillOpacity * opacity) }),
          stroke: new Stroke({
            color: withAlpha(effective.outlineColor, opacity),
            width: effective.outlineWidth,
          }),
        }),
      });
    }

    const strokeOpacity = (effective.lineOpacity ?? effective.fillOpacity) * opacity;
    const dash = parseDashArray(getLineDashArray(isLine ? effective.lineStyle : effective.outlineStyle));

    return new Style({
      stroke: new Stroke({
        color: withAlpha(isLine ? color : effective.outlineColor, isLine ? strokeOpacity : opacity),
        width: effective.outlineWidth,
        lineDash: dash,
      }),
      fill: isLine
        ? undefined
        : new Fill({
            color: withAlpha(color, effective.fillStyle === 'transparent' ? 0 : effective.fillOpacity * opacity),
          }),
    });
  };
}

/**
 * Point clustering (Phase 8, the migration's one genuinely NEW feature —
 * BlueView had no clustering at all before this). Off by default on every
 * layer (`renderer.cluster` unset) — this never changes how an existing
 * layer renders unless an admin explicitly opts it in via the Point
 * symbology panel's own "Group nearby points" toggle.
 *
 * `ol/source/Cluster` wraps a normal point source and groups features
 * within `distance` screen pixels of each other; each resulting "cluster"
 * feature carries its real members under `get('features')` — a single-
 * member cluster is just an ordinary, unclustered point.
 */
function clusterSourceFor(baseSource: VectorSource, renderer: LayerRenderer): VectorSource {
  if (!renderer.cluster) return baseSource;
  return new ClusterSource({ distance: renderer.clusterDistance ?? 40, source: baseSource });
}

/** Three-bucket colour ramp for the 'graduated' cluster marker style — the
 *  classic marker-cluster look (small/green-ish amber, medium orange, large
 *  red), coloured by the cluster's OWN size rather than the layer's colour,
 *  so a big cluster reads as "big" at a glance even across different layers. */
function graduatedClusterColor(count: number): string {
  if (count < 10) return '#F59E0B';
  if (count < 50) return '#F97316';
  return '#DC2626';
}

/** A count-bubble style for a cluster of more than one point — sized by
 *  count (capped, so one layer with a huge cluster doesn't produce a
 *  bubble bigger than the map). 'solid' tints with the renderer's own
 *  colour so a cluster still visually belongs to its layer; 'ring' adds a
 *  soft halo in that same colour; 'graduated' keeps the halo but colours it
 *  by the cluster's own size instead (see graduatedClusterColor). Cached the
 *  same way `pointSymbolStyle` is, for the same reason: this runs per
 *  feature per render. */
const clusterBubbleCache = new globalThis.Map<string, Style | Style[]>();
function clusterBubbleStyle(count: number, renderer: LayerRenderer): Style | Style[] {
  const markerStyle = renderer.clusterMarkerStyle ?? 'solid';
  const key = `${count}|${renderer.color}|${markerStyle}`;
  const cached = clusterBubbleCache.get(key);
  if (cached) return cached;

  const color = markerStyle === 'graduated' ? graduatedClusterColor(count) : renderer.color;
  const radius = Math.min(26, 12 + Math.sqrt(count) * 3);
  const bubble = new Style({
    image: new CircleStyle({
      radius,
      fill: new Fill({ color: withAlpha(color, 0.85) }),
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
    text: new Text({
      text: String(count),
      font: '600 12px sans-serif, system-ui',
      fill: new Fill({ color: '#ffffff' }),
    }),
  });

  let style: Style | Style[];
  if (markerStyle === 'ring' || markerStyle === 'graduated') {
    // The halo is a second, larger, more transparent circle drawn BEHIND the
    // solid one — OL paints an array of styles in order, so this just needs
    // to come first.
    const halo = new Style({
      image: new CircleStyle({
        radius: radius + 6,
        fill: new Fill({ color: withAlpha(color, 0.3) }),
      }),
    });
    style = [halo, bubble];
  } else {
    style = bubble;
  }

  clusterBubbleCache.set(key, style);
  return style;
}

/**
 * Wraps `styleFunctionFor` for a clustered source: a real cluster (more
 * than one member) draws as a count bubble; a single-member "cluster" —
 * which is what every ordinary point becomes once broken apart at a close
 * enough zoom — draws through the EXACT SAME single-symbol/unique-value
 * style every non-clustered point uses, so a fully zoomed-in cluster looks
 * identical to the same layer with clustering turned off.
 */
function styleFunctionForCluster(renderer: LayerRenderer, opacity: number) {
  const pointStyle = styleFunctionFor(renderer, opacity);
  return (feature: FeatureLike): Style | Style[] => {
    const members = feature.get('features') as FeatureLike[] | undefined;
    if (!members) return pointStyle(feature);
    if (members.length === 1) return pointStyle(members[0]);
    return clusterBubbleStyle(members.length, renderer);
  };
}

/** GeoJSON (WGS84) -> OL features in the map's own Web Mercator projection —
 *  every vector source built from server/file GeoJSON goes through this. */
const GEOJSON_FORMAT = new GeoJSONFormat();
function featuresFromGeoJson(collection: GeoJSON.FeatureCollection) {
  return GEOJSON_FORMAT.readFeatures(collection, {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857',
  });
}

/**
 * Fetches and renders each ArcGIS FeatureServer layer id as its own OL
 * vector layer — port of MapView.tsx's `ArcgisFeatureLayer` component.
 * FeatureServers expose no `export`/tile endpoint, so `query` (returning
 * GeoJSON) is the only way to get their data, same as the Leaflet version.
 *
 * Loads every layer id CONCURRENTLY rather than one-at-a-time like the
 * original (which had no stated reason for being sequential) — each still
 * renders as soon as its own fetch resolves, and one failing layer still
 * leaves the rest of the service rendering, matching the original's
 * per-layer try/catch tolerance.
 */
function loadArcgisVectorLayers(
  map: Map,
  baseUrl: string,
  rawArcgisUrl: string | undefined,
  layerIds: number[],
  styles: Record<number, ArcgisLayerStyle>,
  /** Reports each layer's own already-fetched features back to the caller,
   *  for the label overlay — same idea as MapView.tsx's ArcgisFeatureLayer,
   *  so labels never issue a second query for data this effect already has. */
  onFeatures?: (layerId: number, features: GeoJSON.Feature[]) => void,
): () => void {
  let cancelled = false;
  const added: VectorLayer<VectorSource>[] = [];

  const loadOne = async (layerId: number) => {
    try {
      const style = styles[layerId] ?? {};
      const renderer: LayerRenderer = { ...FALLBACK_RENDERER, ...(style.renderer ?? {}) };
      const opacity = style.opacity ?? 1;

      const params = new URLSearchParams({
        where: style.filter?.trim() || '1=1',
        outFields: '*',
        outSR: '4326',
        f: 'geojson',
        resultRecordCount: '2000',
      });

      const response = await fetch(
        withArcgisToken(`${baseUrl}/${layerId}/query?${params.toString()}`, rawArcgisUrl),
      );
      if (!response.ok || cancelled) return;
      const data = await response.json();
      if (cancelled || !data?.features?.length) return;

      const drawable = (data.features as GeoJSON.Feature[]).filter(hasDrawableGeometry);
      if (drawable.length === 0) return;
      onFeatures?.(layerId, drawable);

      const baseSource = new VectorSource({
        features: featuresFromGeoJson({ type: 'FeatureCollection', features: drawable }),
      });
      const layer = new VectorLayer({
        source: clusterSourceFor(baseSource, renderer),
        style: renderer.cluster ? styleFunctionForCluster(renderer, opacity) : styleFunctionFor(renderer, opacity),
        zIndex: VECTOR_ZINDEX,
      });
      if (renderer.cluster) layer.set('bvCluster', true);
      map.addLayer(layer);
      added.push(layer);
    } catch {
      // Skip layers that fail to fetch/parse; the rest of the service still
      // renders — same tolerance as the Leaflet version.
    }
  };

  void Promise.all(layerIds.map(loadOne));

  return () => {
    cancelled = true;
    added.forEach((layer) => map.removeLayer(layer));
  };
}

/** The exact prop contract MapView.tsx exposes — kept as its own named type
 *  so both implementations can be typed against it identically. */
export interface MapViewProps {
  basemap: Basemap;
  center: [number, number];
  zoom: number;
  geoServerUrl: string;
  geoServerLayers: string[];
  arcgisUrl?: string;
  arcgisLayers?: number[];
  arcgisLayerStyles?: Record<number, ArcgisLayerStyle>;
  arcgisSecondaryLayers?: {
    serviceUrl: string;
    layerIds: number[];
    styles: Record<number, ArcgisLayerStyle>;
  }[];
  geoServerLayerStyles?: Record<string, WmsLayerStyle>;
  showScale?: boolean;
  onViewChange?: (center: [number, number], zoom: number) => void;
  onBoundsChange?: (bounds: [number, number, number, number]) => void;
  containerResizeToken?: number | string;
  drawMode?:
    | 'point'
    | 'line'
    | 'polygon'
    | 'circle'
    | 'rectangle'
    | 'freehand-polygon'
    | 'freehand-line'
    | null;
  clearRequest?: number;
  onDrawFeature?: (feature: GeoJSON.Feature) => void;
  onDrawProgress?: (vertexCount: number) => void;
  drawFinishToken?: number;
  /** The Edit tool's open record's own geometry, shown as a highlighted,
   *  non-interactive, dashed preview — see the effect below for why. */
  draftGeometry?: GeoJSON.Geometry | null;
  /** A feature's geometry, draggable as one whole shape — the Edit tool's
   *  "Move" flow (translate, not reshape). */
  moveGeometry?: GeoJSON.Geometry | null;
  onFeatureMove?: (geometry: GeoJSON.Geometry) => void;
  /** Snaps sketching/reshaping to vertices and edges already on the map —
   *  the Edit tool's own toggle. */
  snapEnabled?: boolean;
  measureShapes?: GeoJSON.Feature[];
  measureFormat?: (metres: number) => string;
  searchMarker?: { latitude: number; longitude: number; label: string } | null;
  localLayers?: {
    id: string;
    name?: string;
    data: GeoJSON.FeatureCollection;
    style: ArcgisLayerStyle;
  }[];
  localRasterLayers?: {
    id: string;
    base64: string;
    opacity?: number;
    bounds?: [number, number, number, number];
    mimeType?: string;
  }[];
  /**
   * Every layer's id, in the Layers tab's own top-to-bottom order — what
   * decides draw order among local vector/raster/heatmap layers (see
   * `tocZIndex` below). Ids this doesn't recognise (an ArcGIS/WMS layer
   * resolves separately) just fall back to the flat tier z-index they
   * always had.
   */
  layerOrder?: string[];
  labelLayers?: {
    id: string;
    labeling: LayerLabeling;
    features?: GeoJSON.Feature[];
    arcgisLayerId?: number;
  }[];
  popupConfigs?: Record<string, LayerPopup>;
  onMapPoint?: (point: { lat: number; lng: number }) => void;
  toolOverlay?: GeoJSON.FeatureCollection | null;
  swipe?: SwipeTarget | null;
  selectedFeatures?: GeoJSON.Feature[];
  selectionFlash?: number;
  popupSuppressed?: boolean;
  /** Width:height of the Print tool's page map-frame — shows a red box this
   *  shape, centred over the map, so what the Print/PDF capture will
   *  actually keep is visible BEFORE it's taken, not after. Null/undefined
   *  hides it. See `printFrameAspectRatio` in utils/printSheet.ts. */
  printFrameRatio?: number | null;
}

export default function MapViewOL(props: MapViewProps) {
  const {
    basemap, center, zoom, onViewChange, onBoundsChange, containerResizeToken,
    arcgisUrl = '', arcgisLayers = [], arcgisLayerStyles = {}, arcgisSecondaryLayers = [],
    localLayers = [], localRasterLayers = [],
    geoServerUrl, geoServerLayers, geoServerLayerStyles = {},
    drawMode = null, onDrawFeature, onDrawProgress, drawFinishToken = 0,
    draftGeometry = null, moveGeometry = null, onFeatureMove, snapEnabled = false,
    measureShapes = [], measureFormat,
    toolOverlay = null,
    selectedFeatures = [], selectionFlash = 0,
    swipe = null,
    popupConfigs = {}, onMapPoint, popupSuppressed = false,
    searchMarker = null, showScale = true, labelLayers = [],
    printFrameRatio = null, layerOrder = [],
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const basemapLayerRef = useRef<TileLayer<XYZ> | null>(null);
  /** Read by attachSwipeClip's prerender listeners on every frame — see
   *  that function's own doc comment for why this is a ref, not a value
   *  closed over at listener-registration time. */
  const swipeStateRef = useRef<SwipeClipState | null>(null);
  const [swipePosition, setSwipePosition] = useState(0.5);
  /** Set once the map exists — a real React state (not just `mapRef`) so
   *  effects that must run AFTER the map is created can list it as a
   *  dependency, the same way MapView.tsx's sub-components get `map` for
   *  free from react-leaflet's `useMap()`. */
  const [map, setMap] = useState<Map | null>(null);
  /** Mirrors ViewController's own guard: only push a prop-driven view change
   *  onto the OL view when it did not just come FROM that same view's own
   *  'moveend', or a pan/zoom-drag would fight the very state it produced. */
  const lastAppliedRef = useRef<{ center: [number, number]; zoom: number }>({ center, zoom });

  /* Features an ArcGIS layer has already loaded, so labels do not re-query —
   *  same idea as MapView.tsx's own arcgisFeatures/collectArcgisFeatures. */
  const [arcgisFeatures, setArcgisFeatures] = useState<Record<number, GeoJSON.Feature[]>>({});
  const collectArcgisFeatures = useCallback((layerId: number, features: GeoJSON.Feature[]) => {
    setArcgisFeatures((current) => ({ ...current, [layerId]: features }));
  }, []);

  /** Label inputs with their features resolved, ready for the canvas overlay. */
  const resolvedLabelLayers = useMemo<LabelLayerInput[]>(
    () =>
      labelLayers
        .map((entry) => ({
          id: entry.id,
          labeling: entry.labeling,
          features:
            entry.features ??
            (entry.arcgisLayerId !== undefined ? arcgisFeatures[entry.arcgisLayerId] ?? [] : []),
        }))
        .filter((entry) => entry.features.length > 0),
    [labelLayers, arcgisFeatures],
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.info(
        '[MapViewOL] Phase 13 — every MapView.tsx prop is now wired: basemap, view sync, ArcGIS '
          + 'FeatureServer + local vector layers (single-symbol + unique-value renderer, full point '
          + 'symbol gallery), GeoServer WMS, ArcGIS dynamic/export (MapServer) layers, local '
          + 'raster/GeoTIFF, heatmap, clustering, draw/measure/edit interactions, swipe, '
          + 'click-identify pop-up, scale readout, search marker, client-side labels.',
      );
    }
  }, []);

  // ── Map + view, created once ─────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return undefined;

    const basemapLayer = new TileLayer({
      source: new XYZ({
        url: BASEMAPS[basemap]?.url ?? BASEMAPS.street.url,
        attributions: BASEMAPS[basemap]?.attribution ?? BASEMAPS.street.attribution,
        maxZoom: BASEMAPS[basemap]?.maxNativeZoom ?? BASEMAPS.street.maxNativeZoom,
        // All four basemap servers send Access-Control-Allow-Origin: * — loading
        // through CORS keeps the map's canvas "origin-clean" so the Print tool
        // can read it back. Without this every tile still displays fine, but
        // silently taints the canvas, breaking export with no visible error.
        crossOrigin: 'anonymous',
      }),
      zIndex: BASEMAP_ZINDEX,
    });
    basemapLayerRef.current = basemapLayer;

    const [lat, lng] = center;
    const view = new View({
      center: fromLonLat([lng, lat]),
      zoom,
      maxZoom: MAX_INTERACTIVE_ZOOM,
    });

    const map = new Map({
      target: containerRef.current,
      layers: [basemapLayer],
      view,
      controls: [],
    });
    mapRef.current = map;
    setMap(map);

    const report = () => {
      const newCenter = view.getCenter();
      const newZoom = view.getZoom();
      if (!newCenter || newZoom === undefined) return;
      const [newLng, newLat] = toLonLat(newCenter);
      lastAppliedRef.current = { center: [newLat, newLng], zoom: newZoom };
      onViewChange?.([newLat, newLng], newZoom);

      if (onBoundsChange) {
        const extent: Extent = view.calculateExtent(map.getSize());
        const [west, south] = toLonLat([extent[0], extent[1]]);
        const [east, north] = toLonLat([extent[2], extent[3]]);
        onBoundsChange([west, south, east, north]);
      }
    };
    map.on('moveend', report);
    // Report the first view too, matching ViewController: a map action
    // (e.g. a dashboard filter) applies correctly before any interaction.
    report();

    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
      basemapLayerRef.current = null;
      setMap(null);
    };
    // Intentionally mount-once, mirroring MapContainer's own semantics —
    // basemap/center/zoom changes are handled by the effects below rather
    // than by tearing down and recreating the whole ol/Map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Basemap switch ───────────────────────────────────────────────────
  useEffect(() => {
    const layer = basemapLayerRef.current;
    if (!layer) return;
    const config = BASEMAPS[basemap] ?? BASEMAPS.street;
    layer.setSource(
      new XYZ({
        url: config.url,
        attributions: config.attribution,
        maxZoom: config.maxNativeZoom,
        crossOrigin: 'anonymous',
      }),
    );
  }, [basemap]);

  // ── Controlled center/zoom from outside (e.g. "go to location") ────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const [lat, lng] = center;
    const last = lastAppliedRef.current;
    const changed = last.center[0] !== lat || last.center[1] !== lng || last.zoom !== zoom;
    if (!changed) return;
    lastAppliedRef.current = { center, zoom };
    const view = map.getView();
    view.setCenter(fromLonLat([lng, lat]));
    view.setZoom(zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center[0], center[1], zoom]);

  // ── Container resize (sidebar toggle, window resize, etc.) ─────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const id = window.setTimeout(() => map.updateSize(), 0);
    return () => window.clearTimeout(id);
  }, [containerResizeToken]);

  // ── Swipe divider drag + the ref attachSwipeClip's listeners read ────────
  useEffect(() => {
    swipeStateRef.current = swipe ? { vertical: swipe.vertical, position: swipePosition } : null;
    mapRef.current?.render();
  }, [swipe, swipePosition]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !swipe || !containerRef.current) return undefined;
    const container = containerRef.current;
    let dragging = false;
    const dragPan = map.getInteractions().getArray().find((i) => i instanceof DragPan) as DragPan | undefined;

    const move = (event: PointerEvent) => {
      if (!dragging) return;
      const rect = container.getBoundingClientRect();
      const ratio = swipe.vertical
        ? (event.clientY - rect.top) / rect.height
        : (event.clientX - rect.left) / rect.width;
      setSwipePosition(Math.max(0.02, Math.min(0.98, ratio)));
    };
    const stop = () => {
      dragging = false;
      dragPan?.setActive(true);
    };
    const start = () => {
      dragging = true;
      // Same reasoning as MapView.tsx's own SwipeDivider: panning disabled
      // for the duration so the map doesn't slide along with the handle.
      dragPan?.setActive(false);
    };

    const handle = container.querySelector('[data-bv-swipe-handle]');
    handle?.addEventListener('pointerdown', start);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    return () => {
      handle?.removeEventListener('pointerdown', start);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      dragPan?.setActive(true);
    };
  }, [swipe]);

  // ── ArcGIS FeatureServer vector layers, primary service ─────────────────
  // MapServer/ImageServer (the `else` branch MapView.tsx takes to
  // ArcgisDynamicLayer) is handled by its own effect further down (Phase 5).
  const arcgisLayerIdKey = arcgisLayers.join(',');
  const arcgisStyleKey = JSON.stringify(arcgisLayerStyles);
  useEffect(() => {
    if (!map || !arcgisUrl || arcgisLayers.length === 0) return undefined;
    const baseUrl = getArcgisBaseUrl(arcgisUrl);
    if (!baseUrl || getArcgisServiceKind(baseUrl) !== 'FeatureServer') return undefined;
    return loadArcgisVectorLayers(map, baseUrl, arcgisUrl, arcgisLayers, arcgisLayerStyles, collectArcgisFeatures);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, arcgisUrl, arcgisLayerIdKey, arcgisStyleKey]);

  // ── ArcGIS FeatureServer vector layers, every OTHER service a layer was
  //    added from (Layer.arcgisServiceUrl) — same treatment, once per group.
  const arcgisSecondaryKey = JSON.stringify(arcgisSecondaryLayers);
  useEffect(() => {
    if (!map || arcgisSecondaryLayers.length === 0) return undefined;
    const cleanups = arcgisSecondaryLayers.map((group) => {
      const baseUrl = getArcgisBaseUrl(group.serviceUrl);
      if (!baseUrl || group.layerIds.length === 0) return undefined;
      if (getArcgisServiceKind(baseUrl) !== 'FeatureServer') return undefined; // MapServer -> Phase 5
      return loadArcgisVectorLayers(map, baseUrl, group.serviceUrl, group.layerIds, group.styles, collectArcgisFeatures);
    });
    return () => cleanups.forEach((cleanup) => cleanup?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, arcgisSecondaryKey]);

  // ── Local file-backed vector layers ──────────────────────────────────────
  // Heatmap-configured entries (the Density tool's output) are handled by
  // their own effect below instead — a different OL layer TYPE entirely,
  // not a style variant of this one.
  useEffect(() => {
    if (!map) return undefined;
    const added: VectorLayer<VectorSource>[] = [];
    for (const entry of localLayers) {
      if (entry.style.heatmap) continue;
      const drawable = (entry.data.features ?? []).filter(hasDrawableGeometry);
      if (drawable.length === 0) continue;
      const renderer: LayerRenderer = { ...FALLBACK_RENDERER, ...(entry.style.renderer ?? {}) };
      const opacity = entry.style.opacity ?? 1;
      const baseSource = new VectorSource({
        features: featuresFromGeoJson({ type: 'FeatureCollection', features: drawable }),
      });
      const layer = new VectorLayer({
        source: clusterSourceFor(baseSource, renderer),
        style: renderer.cluster ? styleFunctionForCluster(renderer, opacity) : styleFunctionFor(renderer, opacity),
        zIndex: tocZIndex(entry.id, layerOrder, VECTOR_ZINDEX),
      });
      if (renderer.cluster) layer.set('bvCluster', true);
      map.addLayer(layer);
      added.push(layer);
    }
    return () => added.forEach((layer) => map.removeLayer(layer));
  }, [map, localLayers, layerOrder]);

  // ── Local density/heatmap layers (the Density tool's output) ─────────────
  // `leaflet.heat` exposes no public opacity API — MapView.tsx works around
  // that by reaching into its private `_canvas` field. OL's Heatmap layer
  // has a real, public `opacity` option: a genuine simplification here, not
  // just a port. Radius/blur are passed through as configured — both engines
  // use screen-pixel semantics, but the plan flags these as worth a visual
  // re-tuning pass rather than assuming the numbers transfer exactly.
  useEffect(() => {
    if (!map) return undefined;
    const added: HeatmapLayer[] = [];
    for (const entry of localLayers) {
      const heatmap = entry.style.heatmap;
      if (!heatmap) continue;
      // Same restriction as the original: only Point features contribute —
      // a heatmap has no notion of a line or polygon's weight.
      const pointFeatures = (entry.data.features ?? []).filter(
        (feature) => feature.geometry?.type === 'Point' && hasDrawableGeometry(feature),
      );
      if (pointFeatures.length === 0) continue;

      const source = new VectorSource({
        features: featuresFromGeoJson({ type: 'FeatureCollection', features: pointFeatures }),
      });
      const max = heatmap.max ?? 1;
      const layer = new HeatmapLayer({
        source,
        radius: heatmap.radius,
        blur: heatmap.blur,
        gradient: sampleGradient(heatmap.gradient),
        opacity: entry.style.opacity ?? 1,
        // OL wants a normalised 0-1 weight per feature; leaflet.heat instead
        // took a raw weight and a separate `max` it divided by internally —
        // same maths, just done here instead of inside the plugin.
        weight: (feature) => {
          const raw = feature.get('weight');
          const value = typeof raw === 'number' ? raw : 1;
          return Math.max(0, Math.min(1, value / max));
        },
        zIndex: tocZIndex(entry.id, layerOrder, VECTOR_ZINDEX),
      });
      map.addLayer(layer);
      added.push(layer);
    }
    return () => added.forEach((layer) => map.removeLayer(layer));
  }, [map, localLayers, layerOrder]);

  // ── GeoServer WMS tile layers (data + a second, label-only request) ─────
  // react-leaflet's WMSTileLayer needed a hashed, changed `key` to force a
  // remount whenever styling changed, specifically because Leaflet only
  // reads a layer's `pane` at construction (see MapView.tsx's own long
  // comment on this). OL has no such limitation, so this effect just tears
  // down and rebuilds every WMS layer whenever the layer list or any
  // layer's style changes — simpler, no hash-based remount trick needed.
  const wmsBaseUrl = geoServerUrl ? getWmsBaseUrl(geoServerUrl) : '';
  const geoServerLayerKey = geoServerLayers.join(',');
  const geoServerStyleKey = JSON.stringify(geoServerLayerStyles);
  useEffect(() => {
    if (!map || !wmsBaseUrl || geoServerLayers.length === 0) return undefined;
    const added: TileLayer<TileWMS>[] = [];
    const detachClips: (() => void)[] = [];

    for (const layerName of geoServerLayers) {
      const style = geoServerLayerStyles[layerName] ?? {};
      // GeoServer renders WMS server-side, so symbology is applied by
      // sending a generated SLD document as SLD_BODY — same as MapView.tsx.
      const sld = buildSld(layerName, style.renderer, (style.geometryType ?? 'unknown') as LayerGeometryType);
      const useSld = Boolean(sld) && !isSldTooLarge(sld);
      if (sld && !useSld) {
        // eslint-disable-next-line no-console
        console.error(
          `[symbology] SLD for "${layerName}" exceeds the safe URL length and was skipped. Reduce the number of unique-value classes.`,
        );
      }

      const dataSource = new TileWMS({
        url: wmsBaseUrl,
        params: {
          LAYERS: layerName,
          FORMAT: 'image/png',
          TRANSPARENT: true,
          VERSION: '1.3.0',
          ...(useSld ? { SLD_BODY: sld } : {}),
          ...(style.filter?.trim() ? { CQL_FILTER: style.filter.trim() } : {}),
        },
      });
      const dataLayer = new TileLayer({ source: dataSource, opacity: style.opacity ?? 1, zIndex: WMS_DATA_ZINDEX });
      map.addLayer(dataLayer);
      added.push(dataLayer);
      if (swipe?.wmsLayerName === layerName) detachClips.push(attachSwipeClip(dataLayer, swipeStateRef));

      // Labels ride in a SECOND, transparent request — merging them into
      // the document above would mean inventing a geometry style for
      // layers that have none, which would replace GeoServer's own
      // cartography the moment labels were switched on. No named pane
      // needed the way Leaflet's WMS_LABEL_PANE was — a higher explicit
      // z-index puts it above the data request directly.
      const labelSld = buildLabelSld(layerName, style.labeling, (style.geometryType ?? 'unknown') as LayerGeometryType);
      const useLabelSld = Boolean(labelSld) && !isSldTooLarge(labelSld);
      if (labelSld && !useLabelSld) {
        // eslint-disable-next-line no-console
        console.error(`[labeling] Label SLD for "${layerName}" exceeds the safe URL length and was skipped.`);
      }
      if (useLabelSld) {
        const labelSource = new TileWMS({
          url: wmsBaseUrl,
          params: {
            LAYERS: layerName,
            FORMAT: 'image/png',
            TRANSPARENT: true,
            VERSION: '1.3.0',
            SLD_BODY: labelSld,
            ...(style.filter?.trim() ? { CQL_FILTER: style.filter.trim() } : {}),
          },
        });
        const labelLayer = new TileLayer({ source: labelSource, zIndex: WMS_LABEL_ZINDEX });
        map.addLayer(labelLayer);
        added.push(labelLayer);
      }
    }

    return () => {
      added.forEach((layer) => map.removeLayer(layer));
      detachClips.forEach((detach) => detach());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, wmsBaseUrl, geoServerLayerKey, geoServerStyleKey, swipe]);

  // ── ArcGIS dynamic/export layer (MapServer/ImageServer) ──────────────────
  // Replaces MapView.tsx's `(L.TileLayer as any).extend({ getTileUrl ... })`
  // — a ~50-line hand-rolled subclass reimplementing Leaflet's own tile math
  // (coords -> pixel bounds -> map.unproject -> EPSG:3857 project) purely to
  // turn ArcGIS's bbox-image `export` endpoint into a Leaflet tile layer —
  // with OL's native ol/source/TileArcGISRest, built for exactly this: it
  // derives the bbox/size/dpi/imageSR per tile itself, so none of that math
  // is this file's problem anymore. Net deletion of code, not a port.
  //
  // The token can't be pre-appended to `source.url` the way withArcgisToken
  // normally works: OL appends `/export?...` after whatever `url` already
  // is, and a trailing `?token=...` there would put a path segment after a
  // query string. It goes into `params.token` instead, so OL's own query
  // building appends it correctly alongside LAYERS/LAYERDEFS.
  const arcgisFilterKey = JSON.stringify(arcgisLayerStyles);
  useEffect(() => {
    if (!map || !arcgisUrl || arcgisLayers.length === 0) return undefined;
    const baseUrl = getArcgisBaseUrl(arcgisUrl);
    // Matches MapView.tsx's own fallthrough: only a CONFIRMED FeatureServer
    // takes the vector path (Phase 2); anything else — MapServer, or a kind
    // that couldn't be determined — is treated as exportable imagery here.
    if (!baseUrl || getArcgisServiceKind(baseUrl) === 'FeatureServer') return undefined;

    const layerDefsFor = (ids: number[]) => {
      const defs: Record<string, string> = {};
      for (const id of ids) {
        const filter = arcgisLayerStyles[id]?.filter?.trim();
        if (filter) defs[String(id)] = filter;
      }
      return Object.keys(defs).length > 0 ? JSON.stringify(defs) : undefined;
    };
    const opacityFor = (ids: number[]) =>
      ids.reduce((lowest, id) => Math.min(lowest, arcgisLayerStyles[id]?.opacity ?? 1), 1);
    const token = getArcgisToken(arcgisUrl);
    const buildLayer = (ids: number[], zIndex: number) => {
      const source = new TileArcGISRest({
        url: baseUrl,
        params: {
          LAYERS: `show:${ids.join(',')}`,
          FORMAT: 'png32',
          TRANSPARENT: true,
          DPI: 96,
          ...(layerDefsFor(ids) ? { LAYERDEFS: layerDefsFor(ids) } : {}),
          ...(token ? { token } : {}),
        },
      });
      return new TileLayer({ source, opacity: opacityFor(ids), zIndex });
    };

    const added: TileLayer<TileArcGISRest>[] = [];
    const detachClips: (() => void)[] = [];
    const swipedId = swipe?.arcgisLayerId;
    const isSwiped = swipedId !== null && swipedId !== undefined && arcgisLayers.includes(swipedId);

    if (isSwiped) {
      // One export request draws every enabled sublayer at once, so the
      // swiped one needs to be split into its OWN request — a clip has to
      // have something of its own to clip, same reasoning MapView.tsx's
      // own two-ArcgisDynamicLayer JSX comment gives.
      const others = arcgisLayers.filter((id) => id !== swipedId);
      if (others.length > 0) {
        const layer = buildLayer(others, ARCGIS_DYNAMIC_ZINDEX);
        map.addLayer(layer);
        added.push(layer);
      }
      const swipedLayer = buildLayer([swipedId as number], ARCGIS_DYNAMIC_ZINDEX);
      map.addLayer(swipedLayer);
      added.push(swipedLayer);
      detachClips.push(attachSwipeClip(swipedLayer, swipeStateRef));
    } else {
      const layer = buildLayer(arcgisLayers, ARCGIS_DYNAMIC_ZINDEX);
      map.addLayer(layer);
      added.push(layer);
    }

    return () => {
      added.forEach((layer) => map.removeLayer(layer));
      detachClips.forEach((detach) => detach());
    };
    // `arcgisFilterKey` covers both the layerDefs filter and the group
    // opacity — a changed filter needs a fresh `export` request, since the
    // old image is of the unfiltered data, same reasoning MapView.tsx's own
    // effect dependency list documents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, arcgisUrl, arcgisLayerIdKey, arcgisFilterKey, swipe]);

  // ── Local raster / GeoTIFF layers ────────────────────────────────────────
  // Two shapes, told apart by `mimeType` — see MapView.tsx's LocalRasterLayer
  // doc comment for the full reasoning:
  //   PRESENT (mimeType + bounds): a fresh import, already re-encoded into a
  //   compressed image at a known WGS84 rectangle at import time — the
  //   lightweight, now-standard path for every current import. Nothing left
  //   to decode or reproject; ol/source/ImageStatic just positions the image,
  //   OL's equivalent of Leaflet's L.imageOverlay.
  //   ABSENT: a layer saved before that pre-flattening existed, where
  //   `base64` is still the ORIGINAL GeoTIFF's raw bytes — the legacy
  //   fallback, still real bytes to decode. Ported here with OL's native
  //   ol/source/GeoTIFF (fed through a WebGL tile layer) REPLACING
  //   georaster-layer-for-leaflet, not porting it — OL ships its own decoder
  //   for exactly this, reads the file's own embedded georeferencing/CRS the
  //   same way georaster-layer-for-leaflet did, so no manual reprojection is
  //   needed on either side.
  useEffect(() => {
    if (!map) return undefined;
    const added: (ImageLayer<ImageStatic> | WebGLTileLayer)[] = [];
    const objectUrls: string[] = [];
    const detachClips: (() => void)[] = [];

    for (const entry of localRasterLayers) {
      const swiped = swipe?.localRasterLayerId === entry.id;
      if (entry.mimeType && entry.bounds) {
        const [west, south, east, north] = entry.bounds;
        const extent = transformExtent([west, south, east, north], 'EPSG:4326', 'EPSG:3857');
        const source = new ImageStatic({
          url: `data:${entry.mimeType};base64,${entry.base64}`,
          imageExtent: extent,
        });
        const layer = new ImageLayer({
          source,
          opacity: entry.opacity ?? 1,
          zIndex: tocZIndex(entry.id, layerOrder, VECTOR_ZINDEX),
        });
        map.addLayer(layer);
        added.push(layer);
        if (swiped) detachClips.push(attachSwipeClip(layer, swipeStateRef));
        continue;
      }

      // Legacy path. Fed through an object URL rather than a base64
      // data-URI: GeoTIFFs from this path can be multi-MB, and a Blob OL
      // can read directly is a better fit than a giant data-URI string.
      const blob = new Blob([base64ToArrayBuffer(entry.base64)], { type: 'image/tiff' });
      const objectUrl = URL.createObjectURL(blob);
      objectUrls.push(objectUrl);
      const source = new GeoTIFFSource({ sources: [{ url: objectUrl }] });
      const layer = new WebGLTileLayer({
        source,
        opacity: entry.opacity ?? 1,
        zIndex: tocZIndex(entry.id, layerOrder, VECTOR_ZINDEX),
      });
      map.addLayer(layer);
      added.push(layer);
      if (swiped) detachClips.push(attachSwipeClip(layer, swipeStateRef));
    }

    return () => {
      added.forEach((layer) => map.removeLayer(layer));
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      detachClips.forEach((detach) => detach());
    };
  }, [map, localRasterLayers, swipe, layerOrder]);

  // ── Cluster click-to-expand ───────────────────────────────────────────────
  // A minimal, self-contained default (zoom toward whatever was clicked) —
  // full click ownership between tools is Phase 9/11's concern, so this
  // only acts when the clicked feature is genuinely a multi-member cluster
  // (`bvCluster`-tagged layers only) and leaves every other click alone.
  useEffect(() => {
    if (!map) return undefined;
    const handler = (event: { pixel: number[] }) => {
      const feature = map.forEachFeatureAtPixel(
        event.pixel,
        (found) => found,
        { layerFilter: (layer) => layer.get('bvCluster') === true },
      );
      const members = feature?.get('features') as FeatureLike[] | undefined;
      if (!members || members.length <= 1) return;
      const geometry = (feature as FeatureLike).getGeometry?.();
      const center = (geometry as { getCoordinates?: () => number[] } | undefined)?.getCoordinates?.();
      if (!center) return;
      const view = map.getView();
      view.animate({ center, zoom: (view.getZoom() ?? 10) + 2, duration: 250 });
    };
    map.on('singleclick', handler);
    return () => map.un('singleclick', handler);
  }, [map]);

  // ── Draw tool ─────────────────────────────────────────────────────────
  // Replaces MapView.tsx's DrawController — a ~280-line hand-rolled click/
  // mousemove/dblclick/Escape state machine rebuilding its own rubber-band
  // preview from scratch. OL's ol/interaction/Draw already IS that state
  // machine (including live preview rendering, double-click-to-finish, and
  // Escape-to-abandon, all built in) for Point/LineString/Polygon/Circle
  // natively, and for a rectangle via Circle + createBox()'s geometry
  // function — this is a replacement, not a port, the same pattern as the
  // ArcGIS dynamic layer (Phase 5) and GeoTIFF (Phase 6).
  const drawInteractionRef = useRef<DrawInteraction | null>(null);
  /**
   * `onDrawFeature`/`onDrawProgress` read through refs, kept current on every
   * render, rather than closed over directly by the effect below.
   *
   * MapView.tsx's DrawController was a real mounted component, so `onFeature`
   * was just a prop — always whatever App.tsx's latest `handleSketch` was.
   * `handleSketch` itself is rebuilt on every draw (its own useCallback deps
   * include `layers`, which a completed shape just changed), so a plain
   * `useEffect` here that only re-runs on `[map, drawMode]` would keep
   * calling the FIRST render's `handleSketch` for every shape after the
   * first — each draw still reaches app state correctly (addDrawnFeature
   * itself updates through a functional setState, so it's never actually
   * wrong), but there is no reason to depend on that guarantee holding
   * forever when a ref makes it a non-issue outright.
   */
  const onDrawFeatureRef = useRef(onDrawFeature);
  onDrawFeatureRef.current = onDrawFeature;
  const onDrawProgressRef = useRef(onDrawProgress);
  onDrawProgressRef.current = onDrawProgress;
  useEffect(() => {
    if (!map || !drawMode) return undefined;

    const source = new VectorSource();
    const previewLayer = new VectorLayer({
      source,
      zIndex: 999,
      style: new Style({
        stroke: new Stroke({ color: '#0EA5E9', width: 2.5, lineDash: [5, 4] }),
        fill: new Fill({ color: 'rgba(14, 165, 233, 0.08)' }),
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: '#BAE6FD' }),
          stroke: new Stroke({ color: '#0284C7', width: 2 }),
        }),
      }),
    });
    map.addLayer(previewLayer);

    const olType =
      drawMode === 'point'
        ? 'Point'
        : drawMode === 'line' || drawMode === 'freehand-line'
          ? 'LineString'
          : drawMode === 'polygon' || drawMode === 'freehand-polygon'
            ? 'Polygon'
            : 'Circle'; // circle, rectangle
    // Freehand/curve trace continuously while the pointer is held down,
    // rather than placing one vertex per click — OL's own `freehand` option
    // on the SAME Draw/Point-Line/Polygon machinery, not a different tool.
    const freehand = drawMode === 'freehand-polygon' || drawMode === 'freehand-line';
    const draw = new Draw({
      source,
      type: olType,
      freehand,
      geometryFunction: drawMode === 'rectangle' ? createBox() : undefined,
    });
    drawInteractionRef.current = draw;

    // Live vertex count, for the panel's own "N points placed" / Finish
    // button — mirrors DrawController's onProgress calls.
    draw.on('drawstart', (event) => {
      const geom = event.feature.getGeometry();
      const report = () => {
        const g = geom as { getCoordinates?: () => unknown } | undefined;
        const coords = g?.getCoordinates?.();
        const count = Array.isArray(coords)
          ? drawMode === 'polygon'
            ? ((coords[0] as unknown[] | undefined)?.length ?? 0)
            : (coords as unknown[]).length
          : 1;
        onDrawProgressRef.current?.(count);
      };
      geom?.on('change', report);
      report();
    });

    draw.on('drawend', (event) => {
      try {
        const feature = event.feature;
        // A circle isn't a GeoJSON geometry type — MapView.tsx's own DrawController
        // always finishes a circle as a Polygon ring (`circleRingAround`), so a
        // saved "circle" drawing is portable data, not an engine-specific shape.
        // Matched here rather than writing the native OL Circle geometry out.
        const geom = feature.getGeometry();
        let outGeometry: GeoJSON.Geometry;
        let properties: Record<string, unknown> = {};
        if (drawMode === 'circle' && geom) {
          const circleGeom = geom as unknown as { getCenter: () => number[]; getRadius: () => number };
          const center3857 = circleGeom.getCenter();
          const centerLonLat = toLonLat(center3857);
          const radiusM = circleGeom.getRadius();
          const ring: number[][] = [];
          const segments = 72;
          for (let step = 0; step <= segments; step += 1) {
            const angle = (step / segments) * 2 * Math.PI;
            const point3857 = [center3857[0] + radiusM * Math.cos(angle), center3857[1] + radiusM * Math.sin(angle)];
            ring.push(toLonLat(point3857));
          }
          outGeometry = { type: 'Polygon', coordinates: [ring] };
          properties = { shape: 'circle', radius_m: Math.round(radiusM) };
          void centerLonLat; // kept only for parity/debugging; not part of the output
        } else {
          const written = GEOJSON_FORMAT.writeFeatureObject(feature, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
          }) as GeoJSON.Feature;
          outGeometry = written.geometry;
          properties = { shape: drawMode ?? 'polygon' };
        }
        onDrawFeatureRef.current?.({ type: 'Feature', geometry: outGeometry, properties });
        source.clear();
        onDrawProgressRef.current?.(0);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[MapViewOL] drawend handler threw', err);
      }
    });

    map.addInteraction(draw);

    return () => {
      map.removeInteraction(draw);
      map.removeLayer(previewLayer);
      drawInteractionRef.current = null;
      onDrawProgressRef.current?.(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, drawMode]);

  // The panel's explicit Finish button — OL's Draw interaction exposes this
  // directly, so no custom "collapse near-duplicate points, then close the
  // ring" logic is needed the way DrawController's own finishPending() had.
  useEffect(() => {
    if (drawFinishToken === 0) return;
    drawInteractionRef.current?.finishDrawing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawFinishToken]);

  // ── Draft overlay (the Edit tool's open-record geometry) ─────────────────
  // A brand-new sketch, the feature being reshaped (Esri's own "Reshape
  // Feature" tool sketches a replacement path rather than dragging vertices
  // — the splicing itself is plain GeoJSON math, see
  // utils/reshapeGeometry.ts), or a feature mid-move all have NO other
  // on-map representation until Save writes them into the layer's own
  // data — without this, each would otherwise appear to simply vanish the
  // moment its sketch/drag ends and only reappear after Save. Dashed amber
  // marks it as a highlighted, non-interactive, not-yet-saved preview.
  useEffect(() => {
    if (!map || !draftGeometry) return undefined;

    const written = GEOJSON_FORMAT.readFeature(
      { type: 'Feature', geometry: draftGeometry, properties: {} },
      { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
    ) as OLFeature;
    const layer = new VectorLayer({
      source: new VectorSource({ features: [written] }),
      zIndex: 999,
      style: new Style({
        stroke: new Stroke({ color: '#D97706', width: 3, lineDash: [8, 6] }),
        fill: new Fill({ color: 'rgba(217, 119, 6, 0.12)' }),
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: '#FDE68A' }),
          stroke: new Stroke({ color: '#D97706', width: 2 }),
        }),
      }),
    });
    map.addLayer(layer);

    return () => {
      map.removeLayer(layer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, draftGeometry]);

  // ── Move (the Edit tool's drag-whole-feature flow) ───────────────────────
  // ol/interaction/Translate drags a feature by its current pointer offset
  // without touching any of its vertices — the counterpart to Reshape above,
  // which edits them one at a time. Same offscreen-layer-per-edit pattern.
  useEffect(() => {
    if (!map || !moveGeometry) return undefined;

    const written = GEOJSON_FORMAT.readFeature(
      { type: 'Feature', geometry: moveGeometry, properties: {} },
      { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' },
    ) as OLFeature;
    const source = new VectorSource({ features: [written] });
    const layer = new VectorLayer({
      source,
      zIndex: 999,
      style: new Style({
        stroke: new Stroke({ color: '#F97316', width: 3 }),
        fill: new Fill({ color: 'rgba(249, 115, 22, 0.12)' }),
        image: new CircleStyle({
          radius: 7,
          fill: new Fill({ color: '#FED7AA' }),
          stroke: new Stroke({ color: '#EA580C', width: 3 }),
        }),
      }),
    });
    map.addLayer(layer);

    const translate = new Translate({ layers: [layer] });
    translate.on('translateend', () => {
      const current = source.getFeatures()[0];
      if (!current) return;
      const out = GEOJSON_FORMAT.writeFeatureObject(current, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      }) as GeoJSON.Feature;
      onFeatureMove?.(out.geometry);
    });
    map.addInteraction(translate);

    return () => {
      map.removeInteraction(translate);
      map.removeLayer(layer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, moveGeometry]);

  // ── Snap (the Edit tool's own toggle) ────────────────────────────────────
  // ol/interaction/Snap needs to be added AFTER the interaction(s) it should
  // take priority over (Draw) — OL processes most-recently-added
  // interactions first, so Snap has to be LAST to get first look at the
  // pointer position. Placed after that effect in this file for exactly
  // that reason, and rebuilt (re-added last again) whenever it is itself
  // (re)created, via `drawMode` in the deps below. Also rebuilds whenever
  // the map's own vector layers come and go
  // (an ArcGIS service resolving after this effect first ran, say), via the
  // layers collection's own add/remove events rather than a polling loop.
  //
  // Only the layers holding real, browser-editable vector data are snap
  // sources — filtered by VECTOR_ZINDEX, the z-index ArcGIS FeatureServer
  // and local file-backed layers share, and excluding a clustered layer's
  // source specifically: its features sit at synthetic cluster-centroid
  // positions, not the real geometry, so snapping to them would be wrong.
  useEffect(() => {
    if (!map || !snapEnabled) return undefined;

    let active: Snap[] = [];
    const rebuild = () => {
      active.forEach((snap) => map.removeInteraction(snap));
      const sources = map
        .getLayers()
        .getArray()
        .filter(
          (layer): layer is VectorLayer<VectorSource> =>
            layer instanceof VectorLayer &&
            layer.getZIndex() === VECTOR_ZINDEX &&
            !layer.get('bvCluster'),
        )
        .map((layer) => layer.getSource())
        .filter((source): source is VectorSource => Boolean(source));
      active = sources.map((source) => new Snap({ source }));
      active.forEach((snap) => map.addInteraction(snap));
    };

    rebuild();
    const layersCollection = map.getLayers();
    layersCollection.on('add', rebuild);
    layersCollection.on('remove', rebuild);

    return () => {
      layersCollection.un('add', rebuild);
      layersCollection.un('remove', rebuild);
      active.forEach((snap) => map.removeInteraction(snap));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, snapEnabled, drawMode]);

  // ── Finished measurements, kept on the map ───────────────────────────────
  // Replaces MeasureOverlay. OL redraws a vector layer's styles on every
  // pan/zoom automatically, so — unlike the Leaflet version — no manual
  // `viewTick`-triggered rebuild is needed to keep labels correctly placed.
  useEffect(() => {
    if (!map || measureShapes.length === 0) return undefined;
    const source = new VectorSource({ features: featuresFromGeoJson({ type: 'FeatureCollection', features: measureShapes }) });
    const layer = new VectorLayer({
      source,
      zIndex: 998,
      style: (feature) => {
        const geomType = feature.getGeometry()?.getType();
        const isPolygon = geomType === 'Polygon';
        const isLine = geomType === 'LineString';
        if (!isPolygon && !isLine) return undefined;

        const coords: number[][] = isPolygon
          ? ((feature.getGeometry() as unknown as { getCoordinates: () => number[][][] }).getCoordinates()[0])
          : (feature.getGeometry() as unknown as { getCoordinates: () => number[][] }).getCoordinates();

        const styles: Style[] = [
          // The line/polygon shape itself.
          new Style({
            stroke: new Stroke({ color: '#0F2942', width: isPolygon ? 2.5 : 3 }),
            fill: isPolygon ? new Fill({ color: 'rgba(56, 189, 248, 0.12)' }) : undefined,
          }),
          // A dot at every vertex — a Style's `image` only ever renders at a
          // POINT geometry, so this carries its own MultiPoint built from the
          // same coordinates rather than the feature's own LineString/Polygon
          // geometry (the "a Style can override its geometry" trick this file
          // also uses for label placement).
          new Style({
            geometry: new MultiPoint(coords),
            image: new CircleStyle({
              radius: 4,
              fill: new Fill({ color: '#ffffff' }),
              stroke: new Stroke({ color: '#0F2942', width: 2 }),
            }),
          }),
        ];

        if (measureFormat) {
          let running = 0;
          for (let i = 1; i < coords.length; i += 1) {
            const a = toLonLat(coords[i - 1]);
            const b = toLonLat(coords[i]);
            const length = groundDistance(a, b);
            running += length;
            const mid = [(coords[i - 1][0] + coords[i][0]) / 2, (coords[i - 1][1] + coords[i][1]) / 2];
            styles.push(measureLabelStyle(mid, measureFormat(length), labelRotationRad(coords[i - 1], coords[i])));
          }
          if (isPolygon) {
            const a = toLonLat(coords[coords.length - 1]);
            const b = toLonLat(coords[0]);
            running += groundDistance(a, b);
          }
          if (isLine) {
            styles.push(measureLabelStyle(coords[coords.length - 1], measureFormat(running), 0, true));
          }
        }

        return styles;
      },
    });
    map.addLayer(layer);
    return () => {
      map.removeLayer(layer);
    };
  }, [map, measureShapes, measureFormat]);

  // ── Tool overlay (a fleeting, read-only shape a tool is previewing) ──────
  // A `marker`-tagged point feature (the Coordinates tool's dropped pin, the
  // Trace tool's start/barrier flags) is a labelled PIN, not a plain preview
  // shape — rendered as its own ol/Overlay carrying the exact same
  // .bv-coord-pin/.bv-pin--* markup and CSS the old Leaflet divIcon used, so
  // the visual design carries over unchanged even though the mechanism (an
  // Overlay per pin, not a divIcon) had to. Everything else (buffer rings, a
  // direction line, a plain preview shape) still goes through one shared
  // VectorLayer/Style, same as before.
  useEffect(() => {
    if (!map || !toolOverlay || toolOverlay.features.length === 0) return undefined;

    const shapeFeatures = toolOverlay.features.filter((feature) => !feature.properties?.marker);
    const pinFeatures = toolOverlay.features.filter((feature) => feature.properties?.marker);

    let layer: VectorLayer<VectorSource> | undefined;
    if (shapeFeatures.length > 0) {
      const source = new VectorSource({
        features: featuresFromGeoJson({ type: 'FeatureCollection', features: shapeFeatures }),
      });
      layer = new VectorLayer({
        source,
        zIndex: 997,
        style: new Style({
          stroke: new Stroke({ color: '#2563EB', width: 2, lineDash: [6, 4] }),
          fill: new Fill({ color: 'rgba(37, 99, 235, 0.08)' }),
          image: new CircleStyle({
            radius: 5,
            fill: new Fill({ color: '#ffffff' }),
            stroke: new Stroke({ color: '#2563EB', width: 2 }),
          }),
        }),
      });
      map.addLayer(layer);
    }

    const pinOverlays = pinFeatures
      .map((feature) => {
        if (feature.geometry?.type !== 'Point') return null;
        const [lon, lat] = feature.geometry.coordinates;
        const properties = feature.properties ?? {};

        const element = document.createElement('div');
        element.className = 'bv-coord-pin';
        const body = document.createElement('span');
        body.className = `bv-coord-pin__body${properties.pin ? ` bv-pin--${properties.pin}` : ''}`;
        body.textContent = String(properties.marker ?? '');
        element.appendChild(body);
        if (properties.label) {
          const label = document.createElement('span');
          label.className = 'bv-coord-pin__label';
          label.textContent = String(properties.label);
          element.appendChild(label);
        }

        // 'top-left', matching Leaflet's own `iconAnchor: [0, 0]` — the
        // element's top-left corner IS the coordinate, and .bv-coord-pin__body
        // /__label position themselves from there via absolute CSS offsets.
        const overlay = new Overlay({ element, positioning: 'top-left', stopEvent: false });
        overlay.setPosition(fromLonLat([lon, lat]));
        map.addOverlay(overlay);
        return overlay;
      })
      .filter((overlay): overlay is Overlay => overlay !== null);

    return () => {
      if (layer) map.removeLayer(layer);
      pinOverlays.forEach((overlay) => map.removeOverlay(overlay));
    };
  }, [map, toolOverlay]);

  // ── Attribute-table selection highlight ──────────────────────────────────
  // Toggling `layer.setVisible()` twice is OL's equivalent of MapView.tsx's
  // add/remove-from-map blink trick — simpler here, since a layer already
  // has one visibility flag to flip rather than a whole group to detach.
  useEffect(() => {
    if (!map || selectedFeatures.length === 0) return undefined;
    const source = new VectorSource({ features: featuresFromGeoJson({ type: 'FeatureCollection', features: selectedFeatures }) });
    const layer = new VectorLayer({
      source,
      zIndex: 996,
      style: [
        new Style({
          stroke: new Stroke({ color: '#67E8F9', width: 12 }),
          image: new CircleStyle({ radius: 13, stroke: new Stroke({ color: '#67E8F9', width: 7 }) }),
        }),
        new Style({
          stroke: new Stroke({ color: '#0891B2', width: 3.5 }),
          image: new CircleStyle({ radius: 7, stroke: new Stroke({ color: '#0891B2', width: 3.5 }) }),
        }),
      ],
    });
    map.addLayer(layer);
    return () => {
      map.removeLayer(layer);
    };
  }, [map, selectedFeatures]);

  useEffect(() => {
    if (!map || selectionFlash === 0) return undefined;
    // Re-finds the layer just added by the effect above via its z-index —
    // simplest way to reach it without threading an extra ref through two
    // effects that already both depend on `selectedFeatures`.
    const layers = map.getLayers().getArray().filter((l) => l.getZIndex() === 996);
    const STEP = 170;
    const timers = [
      window.setTimeout(() => layers.forEach((l) => l.setVisible(false)), STEP),
      window.setTimeout(() => layers.forEach((l) => l.setVisible(true)), STEP * 2),
      window.setTimeout(() => layers.forEach((l) => l.setVisible(false)), STEP * 3),
      window.setTimeout(() => layers.forEach((l) => l.setVisible(true)), STEP * 4),
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [map, selectionFlash]);

  /**
   * "Zoom to selected" actually frames what was selected, same trigger as
   * the blink above. Deliberately NOT a fixed zoom level read off `center`/
   * `zoom` props: those are plain numbers App.tsx cannot correctly compute
   * for an arbitrary line/polygon without knowing the real, on-screen pixel
   * size of the map — only the OL view itself has that, which is exactly
   * what `view.fit` uses. A single fixed zoom (as App.tsx used to apply
   * directly) worked fine for a point or a short line but cropped anything
   * whose extent did not fit on screen at that zoom.
   *
   * `maxZoom` keeps the familiar behaviour for anything SMALL enough to fit
   * well inside it already (a point still lands at the same predictable
   * ~1:1000 scale every other "zoom to" in this app uses) while still
   * zooming OUT further, automatically, for anything that needs more room
   * — the one behaviour this is fixing. Mirrors `zoomToActivePopupFeature`'s
   * own point/non-point split just above, the already-proven pattern for
   * "frame this geometry properly" in this file.
   */
  useEffect(() => {
    if (!map || selectionFlash === 0 || selectedFeatures.length === 0) return undefined;

    const extent = createEmpty();
    for (const feature of selectedFeatures) {
      if (!feature.geometry) continue;
      try {
        const geomExtent = GEOJSON_FORMAT.readGeometry(feature.geometry as GeoJSON.GeoJsonObject, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        }).getExtent();
        extend(extent, geomExtent);
      } catch {
        // One malformed geometry among the selection must not block framing
        // the rest of it.
      }
    }
    if (isEmpty(extent) || !extent.every((value) => Number.isFinite(value))) return undefined;

    const view = map.getView();
    const center = getCenter(extent);
    const [, centerLat] = toLonLat(center);
    const maxZoom = zoomForScale(centerLat, ZOOM_TO_SELECTION_SCALE);

    const isPointLike = extent[2] - extent[0] < 1 && extent[3] - extent[1] < 1;
    if (isPointLike) {
      view.animate({ center, zoom: maxZoom });
    } else {
      view.fit(extent, { padding: [64, 64, 64, 64], maxZoom });
    }
    return undefined;
  }, [map, selectionFlash]);

  // ── Click-identify pop-up ─────────────────────────────────────────────
  // Same GeoServer WMS / ArcGIS REST / client-side-hit-test funnel as
  // MapView.tsx's FeatureInfoController, folded into this one component
  // rather than split out, since OL gives every hook here direct access to
  // `map` already (no react-leaflet `useMap()` to route around).
  const [popupContainer] = useState(() => document.createElement('div'));
  const popupOverlayRef = useRef<Overlay | null>(null);
  const requestIdRef = useRef(0);
  const pendingSpinnerRef = useRef<number | undefined>(undefined);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupResults, setPopupResults] = useState<PopupFeature[]>([]);
  const [popupError, setPopupError] = useState<string | null>(null);
  const [popupIndex, setPopupIndex] = useState(0);
  const [popupExpanded, setPopupExpanded] = useState(false);
  /** Same drag-offset-as-CSS-transform trick as MapView.tsx's own pop-up —
   *  see that component's doc comment on `dragOffset` for why. */
  const [popupDragOffset, setPopupDragOffset] = useState({ x: 0, y: 0 });
  const popupDragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(
    null,
  );

  // The overlay itself, created once the map exists.
  //
  // `stopEvent: true` (the default) is OL's own answer to the problem
  // MapView.tsx's popup works around by hand (see FeatureInfoController's
  // long comment on `container`): it stops a click that starts inside the
  // overlay's DOM element from ever reaching the map's own click dispatch,
  // so Close/Expand/paging buttons can never re-trigger identify the way an
  // unmounting Leaflet popup could. Nothing extra needed here for that.
  useEffect(() => {
    if (!map) return undefined;
    const overlay = new Overlay({
      element: popupContainer,
      positioning: 'bottom-center',
      offset: [0, -14],
      autoPan: { animation: { duration: 250 } },
    });
    map.addOverlay(overlay);
    popupOverlayRef.current = overlay;
    return () => {
      map.removeOverlay(overlay);
      popupOverlayRef.current = null;
    };
  }, [map, popupContainer]);

  const closeIdentifyPopup = useCallback(() => {
    requestIdRef.current += 1; // invalidate any in-flight response
    window.clearTimeout(pendingSpinnerRef.current);
    setPopupResults([]);
    setPopupError(null);
    setPopupLoading(false);
    setPopupDragOffset({ x: 0, y: 0 });
    popupOverlayRef.current?.setPosition(undefined);
  }, []);

  const openIdentifyPopupAt = useCallback((coordinate: number[]) => {
    setPopupDragOffset({ x: 0, y: 0 });
    popupOverlayRef.current?.setPosition(coordinate);
  }, []);

  /* Stored configurations are normalised on read rather than on write: this
     data round-trips through localStorage and may predate the current shape. */
  const popupConfigFor = useCallback(
    (key: string): LayerPopup | null => {
      const stored = popupConfigs[key];
      return stored ? normalisePopup(stored) : null;
    },
    [popupConfigs],
  );
  const popupEnabledFor = useCallback(
    (key: string) => popupConfigFor(key)?.enabled !== false,
    [popupConfigFor],
  );

  const identifySuppressed = Boolean(drawMode) || popupSuppressed;

  useEffect(() => {
    if (!map) return undefined;

    const handleClick = async (event: MapBrowserEvent<PointerEvent>) => {
      const [lon, lat] = toLonLat(event.coordinate);
      // Fires on every raw click regardless of suppression or the popup's own
      // state, same as MapView.tsx's separate, unconditional ClickReporter.
      onMapPoint?.({ lat, lng: lon });

      // A tool has the map's clicks (sketching, the Coordinates readout,
      // Identify's own panel) — placing a sketch vertex is not a request to
      // identify what's underneath it.
      if (identifySuppressed) return;

      const wmsBaseUrlNow = geoServerUrl ? getWmsBaseUrl(geoServerUrl) : '';
      const arcgisBaseUrlNow = getArcgisBaseUrl(arcgisUrl);

      const wmsTargets = wmsBaseUrlNow
        ? geoServerLayers.filter((name) => popupEnabledFor(wmsPopupKey(name)))
        : [];
      const arcgisTargets = arcgisUrl
        ? arcgisLayers.filter((id) => popupEnabledFor(arcgisPopupKey(id)))
        : [];
      const arcgisSecondaryTargets = arcgisSecondaryLayers.flatMap((group) =>
        group.layerIds
          .filter((id) => popupEnabledFor(arcgisPopupKey(id)))
          .map((id) => ({ id, group })),
      );
      const localTargets = localLayers.filter((entry) => popupEnabledFor(localPopupKey(entry.id)));

      /* Imported layers are resolved HERE, synchronously, before any request
         goes out: their features are already in the browser. */
      const localHits: { key: string; result: FeatureQueryResult }[] = [];
      for (const entry of localTargets) {
        const hits = hitTestFeaturesOL(map, [lon, lat], entry.data.features ?? []);
        for (const feature of hits) {
          localHits.push({
            key: localPopupKey(entry.id),
            result: {
              layerTitle: entry.name || 'Imported layer',
              sourceType: 'geoserver',
              attributes: (feature.properties ?? {}) as Record<string, unknown>,
              aliases: {},
              capabilities: LOCAL_CAPABILITIES,
              geometry: feature.geometry ?? undefined,
            },
          });
        }
      }

      if (
        wmsTargets.length === 0 &&
        arcgisTargets.length === 0 &&
        arcgisSecondaryTargets.length === 0 &&
        localTargets.length === 0
      ) {
        return;
      }

      const requestId = ++requestIdRef.current;
      setPopupResults([]);
      setPopupError(null);
      setPopupIndex(0);

      window.clearTimeout(pendingSpinnerRef.current);
      if (wmsTargets.length > 0 || arcgisTargets.length > 0 || arcgisSecondaryTargets.length > 0) {
        pendingSpinnerRef.current = window.setTimeout(() => {
          if (requestId !== requestIdRef.current) return;
          setPopupLoading(true);
          openIdentifyPopupAt(event.coordinate);
        }, SPINNER_DELAY_MS);
      }

      const resolution = map.getView().getResolution() ?? 1;

      const requests: { key: string; query: Promise<FeatureQueryResult[]> }[] = [
        ...wmsTargets.map((layerName) => ({
          key: wmsPopupKey(layerName),
          query: queryGeoServerWmsOL(wmsBaseUrlNow, layerName, event.coordinate, resolution),
        })),
        ...arcgisTargets.map((layerId) => ({
          key: arcgisPopupKey(layerId),
          query: queryEsriRestOL(arcgisBaseUrlNow, layerId, lon, lat, resolution, arcgisUrl),
        })),
        ...arcgisSecondaryTargets.map(({ id, group }) => ({
          key: arcgisPopupKey(id),
          query: queryEsriRestOL(
            getArcgisBaseUrl(group.serviceUrl),
            id,
            lon,
            lat,
            resolution,
            group.serviceUrl,
          ),
        })),
      ];

      // allSettled, not all: one failing layer must not discard results from
      // the others.
      const outcomes = await Promise.allSettled(requests.map((request) => request.query));
      if (requestId !== requestIdRef.current) return; // a newer click superseded this

      const settled: { key: string; result: FeatureQueryResult }[] = [...localHits];
      let succeededCount = 0;
      outcomes.forEach((outcome, index) => {
        if (outcome.status !== 'fulfilled') return;
        succeededCount += 1;
        for (const result of outcome.value) settled.push({ key: requests[index].key, result });
      });
      const allFailed = requests.length > 0 && succeededCount === 0 && localHits.length === 0;

      const formatted: PopupFeature[] = settled.map(({ key, result }, index) => {
        const { metadata, remaining } = extractMetadata(result.attributes);
        const base = formatFeatureForPopup(remaining, {
          id: `${result.layerTitle}-${index}`,
          layerTitle: result.layerTitle,
          sourceType: result.sourceType,
          aliases: result.aliases,
        });

        const config = popupConfigFor(key);
        if (!config) {
          return {
            ...base,
            metadata,
            capabilities: result.capabilities,
            displayTitle: result.layerTitle,
            geometry: result.geometry,
          };
        }

        return {
          ...base,
          fields: resolvePopupFields(remaining, result.aliases, config),
          metadata,
          capabilities: result.capabilities,
          displayTitle: resolvePopupTitle(config.title, result.attributes, result.layerTitle),
          geometry: result.geometry,
        };
      });

      window.clearTimeout(pendingSpinnerRef.current);
      setPopupLoading(false);

      if (formatted.length === 0) {
        if (allFailed) {
          setPopupError('Unable to retrieve feature information.');
          openIdentifyPopupAt(event.coordinate);
          return;
        }
        closeIdentifyPopup();
        return;
      }

      openIdentifyPopupAt(event.coordinate);
      setPopupError(null);
      setPopupResults(formatted);
    };

    map.on('singleclick', handleClick);
    return () => {
      map.un('singleclick', handleClick);
    };
  }, [
    map, identifySuppressed, onMapPoint, geoServerUrl, geoServerLayers, arcgisUrl, arcgisLayers,
    arcgisSecondaryLayers, localLayers, popupEnabledFor, popupConfigFor, closeIdentifyPopup,
    openIdentifyPopupAt,
  ]);

  // A tool taking over while a card is open closes it.
  useEffect(() => {
    if (identifySuppressed) closeIdentifyPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifySuppressed]);

  // Escape closes the popup.
  useEffect(() => {
    const onKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key === 'Escape') closeIdentifyPopup();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closeIdentifyPopup]);

  useEffect(() => {
    return () => window.clearTimeout(pendingSpinnerRef.current);
  }, []);

  const activePopupFeature: PopupFeature | undefined = popupResults[popupIndex];

  // ── Identify pop-up highlight ─────────────────────────────────────────────
  // Its own layer, independent of the Attribute-table/Select-tool selection
  // highlight above. This USED TO publish the pop-up's feature back out
  // through `onIdentifySelection` so the parent could feed it into that same
  // `selectedFeatures` prop — meaning every plain click-to-identify (or
  // closing the pop-up) silently overwrote or cleared whatever a Select tool
  // had just selected, since both were really one piece of state with two
  // unrelated writers. Reading `activePopupFeature` straight from this
  // component's own state instead needs no round trip through the parent at
  // all, and the two highlights can no longer clobber each other.
  useEffect(() => {
    const geometry = activePopupFeature?.geometry;
    if (!map || !geometry) return undefined;
    const source = new VectorSource({
      features: featuresFromGeoJson({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', properties: {}, geometry: geometry as GeoJSON.Geometry }],
      }),
    });
    const layer = new VectorLayer({
      source,
      zIndex: 995,
      style: [
        new Style({
          stroke: new Stroke({ color: '#67E8F9', width: 12 }),
          image: new CircleStyle({ radius: 13, stroke: new Stroke({ color: '#67E8F9', width: 7 }) }),
        }),
        new Style({
          stroke: new Stroke({ color: '#0891B2', width: 3.5 }),
          image: new CircleStyle({ radius: 7, stroke: new Stroke({ color: '#0891B2', width: 3.5 }) }),
        }),
      ],
    });
    map.addLayer(layer);
    return () => {
      map.removeLayer(layer);
    };
  }, [map, activePopupFeature]);

  const zoomToActivePopupFeature = useCallback(() => {
    if (!map) return;
    const geometry = activePopupFeature?.geometry;
    if (!geometry) return;

    const olGeometry = GEOJSON_FORMAT.readGeometry(geometry as GeoJSON.GeoJsonObject, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });
    const extent = olGeometry.getExtent();
    if (!extent.every((value) => Number.isFinite(value))) return;

    const view = map.getView();
    if (geometry.type === 'Point') {
      view.animate({ center: getCenter(extent), zoom: Math.max(view.getZoom() ?? 0, 17) });
      return;
    }
    view.fit(extent, { padding: [48, 48, 48, 48], maxZoom: 18 });
  }, [map, activePopupFeature]);

  const popupBody = popupLoading ? (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white px-5 py-6 shadow-lg">
      <div className="flex items-center gap-2.5">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent" />
        <span className="text-sm font-medium text-slate-600">Loading feature info…</span>
      </div>
    </div>
  ) : popupError ? (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white px-5 py-4 shadow-lg">
      <p className="text-sm font-medium text-red-600">{popupError}</p>
      <button
        type="button"
        onClick={closeIdentifyPopup}
        className="mt-2 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
      >
        Dismiss
      </button>
    </div>
  ) : activePopupFeature ? (
    <div
      style={{
        width: popupExpanded ? 560 : 344,
        transform:
          popupDragOffset.x || popupDragOffset.y
            ? `translate(${popupDragOffset.x}px, ${popupDragOffset.y}px)`
            : undefined,
      }}
    >
      <FeatureAttributePopup
        title={activePopupFeature.displayTitle}
        fields={activePopupFeature.fields.map((entry) => ({ field: entry.label, value: entry.value }))}
        metadata={activePopupFeature.metadata}
        size={popupExpanded ? 'default' : 'compact'}
        maxTableHeight={popupExpanded ? 420 : 200}
        collapsedRowCount={popupExpanded ? 14 : 7}
        expanded={popupExpanded}
        onExpand={() => setPopupExpanded((current) => !current)}
        onHeaderPointerDown={(event) => {
          if ((event.target as HTMLElement).closest('button')) return;
          event.preventDefault();
          popupDragRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            originX: popupDragOffset.x,
            originY: popupDragOffset.y,
          };
        }}
        onZoomTo={activePopupFeature.geometry ? zoomToActivePopupFeature : undefined}
        onClose={closeIdentifyPopup}
        pager={
          popupResults.length > 1
            ? {
                index: popupIndex,
                total: popupResults.length,
                label: activePopupFeature.layerTitle,
                onPrevious: () => setPopupIndex((current) => Math.max(0, current - 1)),
                onNext: () => setPopupIndex((current) => Math.min(popupResults.length - 1, current + 1)),
              }
            : undefined
        }
      />
    </div>
  ) : null;

  // The pop-up card's drag, window-scoped for the same reason the swipe
  // divider's drag is: the cursor routinely outruns a listener scoped to the
  // card itself once a drag is underway.
  useEffect(() => {
    const move = (event: PointerEvent) => {
      const drag = popupDragRef.current;
      if (!drag) return;
      setPopupDragOffset({
        x: drag.originX + (event.clientX - drag.startX),
        y: drag.originY + (event.clientY - drag.startY),
      });
    };
    const stop = () => {
      popupDragRef.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
  }, []);

  // ── Live scale readout, and typing a scale to jump straight to it ──────
  const [scaleDenominator, setScaleDenominator] = useState<number | null>(null);
  useEffect(() => {
    if (!map || !showScale) {
      setScaleDenominator(null);
      return undefined;
    }
    const update = () => {
      const view = map.getView();
      const [, lat] = toLonLat(view.getCenter() ?? [0, 0]);
      setScaleDenominator(scaleAt(lat, view.getZoom() ?? 0));
    };
    update();
    map.on('moveend', update);
    return () => {
      map.un('moveend', update);
    };
  }, [map, showScale]);

  const [editingScale, setEditingScale] = useState(false);
  const [scaleInputText, setScaleInputText] = useState('');

  const startEditingScale = () => {
    if (scaleDenominator === null) return;
    setScaleInputText(String(Math.round(scaleDenominator)));
    setEditingScale(true);
  };

  /** Jumps the view to whatever scale was typed — the OL view's own
   *  `moveend` (see the effect above) reports the result back up through
   *  `onViewChange` exactly as any other pan/zoom does, so App.tsx's
   *  center/zoom state and this readout both stay in step without a second,
   *  separate sync path. */
  const commitScaleEdit = () => {
    setEditingScale(false);
    const parsed = parseScale(scaleInputText);
    if (parsed <= 0 || !mapRef.current) return;
    const view = mapRef.current.getView();
    const [, lat] = toLonLat(view.getCenter() ?? [0, 0]);
    const targetZoom = Math.max(0, Math.min(MAX_INTERACTIVE_ZOOM, zoomForScale(lat, parsed)));
    view.animate({ zoom: targetZoom, duration: 250 });
  };

  // ── Search-result marker ──────────────────────────────────────────────
  // An OL Overlay carrying the same pin markup MapView.tsx builds via
  // `L.divIcon` — the hover tooltip becomes a native `title` attribute
  // rather than a synthesised DOM tooltip, the one deliberate simplification
  // here (flagged for manual QA, not a silent behaviour change of substance).
  useEffect(() => {
    if (!map || !searchMarker) return undefined;
    const element = document.createElement('div');
    element.style.position = 'relative';
    element.style.width = '26px';
    element.style.height = '34px';
    element.title = searchMarker.label;
    element.innerHTML =
      '<svg viewBox="0 0 24 32" width="26" height="34" aria-hidden="true">' +
      '<path d="M12 0C5.7 0 .7 5 .7 11.3.7 19.8 12 32 12 32s11.3-12.2 11.3-20.7C23.3 5 18.3 0 12 0z" ' +
      'fill="#2563EB" stroke="#FFFFFF" stroke-width="2"/>' +
      '<circle cx="12" cy="11" r="4" fill="#FFFFFF"/></svg>';

    const overlay = new Overlay({
      element,
      positioning: 'bottom-center',
      offset: [0, 4],
      stopEvent: false,
    });
    overlay.setPosition(fromLonLat([searchMarker.longitude, searchMarker.latitude]));
    map.addOverlay(overlay);
    return () => {
      map.removeOverlay(overlay);
    };
  }, [map, searchMarker]);

  // ── Client-side label overlay ────────────────────────────────────────
  // One canvas, redrawn on every 'postrender' — dramatically simpler than
  // MapView.tsx's own LabelOverlay.tsx, whose entire doc comment is about
  // working around Leaflet's pane-transform stacking-context problem (a
  // canvas outside the transformed pane hierarchy can never sit BETWEEN two
  // panes by z-index alone, only above or below the whole stack). OL has no
  // such split: every layer canvas AND both overlay containers (the pop-up's
  // among them) are plain siblings inside one `.ol-viewport`, painted in DOM
  // order — so "above the map layers, below the pop-up" is just "insert this
  // canvas right before the first overlay container", no pane and no
  // screen-space origin-shift dance needed. And since nothing here is
  // CSS-transformed for zoom animation (OL applies that per-layer-canvas,
  // not to the viewport), there's no Leaflet-style hide-during-zoom trick
  // either — 'postrender' already fires with the current interpolated view
  // on every animation frame, so recomputing pixel positions there alone
  // keeps labels glued to their features throughout a zoom or pan.
  const resolvedLabelLayersRef = useRef<LabelLayerInput[]>(resolvedLabelLayers);
  resolvedLabelLayersRef.current = resolvedLabelLayers;

  useEffect(() => {
    if (!map) return undefined;
    const viewport = map.getViewport();
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.pointerEvents = 'none';
    const firstOverlayContainer = viewport.querySelector<HTMLElement>(
      '.ol-overlaycontainer-stopevent, .ol-overlaycontainer',
    );
    if (firstOverlayContainer) viewport.insertBefore(canvas, firstOverlayContainer);
    else viewport.appendChild(canvas);

    let disposed = false;

    const paint = () => {
      if (disposed) return;
      const size = map.getSize();
      if (!size) return;
      const [width, height] = size;
      const ratio = window.devicePixelRatio || 1;
      const context = canvas.getContext('2d');
      if (!context) return;

      // Resizing the backing store clears it, so only touch it when it changed.
      if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
      }
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);

      const active = resolvedLabelLayersRef.current.filter((entry) => labelingIsActive(entry.labeling));
      if (active.length === 0) return;

      // Real cartographic scale, from the view's own latitude — see
      // ScaleVisibility.ts for why this is not derived from the zoom level.
      const view = map.getView();
      const [, centerLat] = toLonLat(view.getCenter() ?? [0, 0]);
      const scale = scaleAt(centerLat, view.getZoom() ?? 0);

      // Straight to viewport pixel space — no origin-shift needed, see the
      // effect's own doc comment above.
      const project: Project = (lon, lat) => {
        const pixel = map.getPixelFromCoordinate(fromLonLat([lon, lat]));
        return pixel ? { x: pixel[0], y: pixel[1] } : { x: -9999, y: -9999 };
      };

      const items: LabelDrawItem[] = [];
      for (const entry of active) {
        for (const labelClass of entry.labeling.classes) {
          if (!labelClass.field.trim() && !labelClass.expression.trim()) continue;
          if (!isWithinScale(scale, labelClass.scale)) continue;

          for (const feature of entry.features) {
            const properties = (feature.properties ?? {}) as Record<string, unknown>;
            if (!passesLabelFilter(labelClass.filter, properties)) continue;

            const text = resolveLabelText(labelClass, properties);
            if (!text) continue;

            const anchors = anchorsForGeometry(feature.geometry, project, labelClass.placement.repeat, {
              width,
              height,
            });
            for (const anchor of anchors) items.push({ text, anchor, labelClass });
          }
        }
      }

      drawLabels(context, items, { width, height });
    };

    map.on('postrender', paint);
    paint();

    return () => {
      disposed = true;
      map.un('postrender', paint);
      canvas.remove();
    };
  }, [map]);

  /* Repaint when the configuration or the feature set changes. The effect
   *  above owns the canvas; `map.render()` just asks OL for a fresh frame,
   *  which fires 'postrender' the same as a real pan or zoom would. */
  useEffect(() => {
    map?.render();
  }, [map, resolvedLabelLayers]);

  // ── Print frame box (the Print tool's extent preview) ────────────────────
  // As large as it can be while staying inside the container at the
  // requested ratio, and centred — a plain pixel computation rather than a
  // CSS aspect-ratio trick because `buildSheet` (App.tsx) needs to read this
  // exact box back via `[data-print-frame]`'s own `getBoundingClientRect()`
  // to crop the capture to it, so the number driving the crop and the
  // number driving what's drawn on screen must be the same one.
  const [printFrameBox, setPrintFrameBox] = useState<
    { left: number; top: number; width: number; height: number } | null
  >(null);
  useEffect(() => {
    const node = containerRef.current;
    if (!node || !printFrameRatio) {
      setPrintFrameBox(null);
      return undefined;
    }
    const compute = () => {
      const rect = node.getBoundingClientRect();
      const fit = 0.92; // a thin gap so the dashed border never hugs the edge
      let width = rect.width * fit;
      let height = width / printFrameRatio;
      if (height > rect.height * fit) {
        height = rect.height * fit;
        width = height * printFrameRatio;
      }
      setPrintFrameBox({ left: (rect.width - width) / 2, top: (rect.height - height) / 2, width, height });
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(node);
    return () => observer.disconnect();
  }, [printFrameRatio]);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
      {/* The Print tool's extent preview — everything outside the box is
       *  what Print/PDF will crop away, shown before capture rather than
       *  discovered after it. Purely visual: panning/zooming the real map
       *  underneath is how the admin repositions what falls inside it. */}
      {printFrameBox && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-[1150] overflow-hidden">
          <div
            data-print-frame
            style={{
              position: 'absolute',
              left: printFrameBox.left,
              top: printFrameBox.top,
              width: printFrameBox.width,
              height: printFrameBox.height,
              border: '2px dashed #dc2626',
              boxShadow: '0 0 0 2000px rgba(15, 23, 42, 0.32)',
            }}
          >
            <span className="absolute -top-6 left-0 whitespace-nowrap rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow">
              Print area — pan the map to position it
            </span>
          </div>
        </div>
      )}
      {/* The Swipe divider — a plain DOM overlay dragged via pointer events,
       *  same approach as MapView.tsx's own SwipeDivider, just reading/
       *  writing `swipePosition` instead of Leaflet pane CSS. */}
      {swipe && (
        <div
          data-bv-swipe-handle
          role="separator"
          aria-orientation={swipe.vertical ? 'horizontal' : 'vertical'}
          aria-label="Swipe divider"
          className="absolute z-[1200] flex items-center justify-center"
          style={{
            ...(swipe.vertical
              ? { left: 0, right: 0, top: `${swipePosition * 100}%`, height: 22, marginTop: -11, cursor: 'ns-resize' }
              : { top: 0, bottom: 0, left: `${swipePosition * 100}%`, width: 22, marginLeft: -11, cursor: 'ew-resize' }),
            touchAction: 'none',
          }}
        >
          <span
            aria-hidden="true"
            className="bg-white/90 shadow-md"
            style={swipe.vertical ? { height: 2, width: '100%' } : { width: 2, height: '100%' }}
          />
          <span
            aria-hidden="true"
            className="absolute flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-500 shadow-md"
          >
            <span className="text-[11px] font-bold leading-none">{swipe.vertical ? '↕' : '↔'}</span>
          </span>
        </div>
      )}
      {showScale && scaleDenominator !== null && (
        <div
          className="gl-toolbar absolute bottom-4 right-4 z-[1100] flex items-baseline gap-1.5 px-3 py-1.5 text-xs text-slate-600"
          title={editingScale ? undefined : 'Click to jump to a scale'}
        >
          <span className="text-[10px] uppercase tracking-wide text-slate-400">Scale</span>
          {editingScale ? (
            <span className="flex items-baseline gap-1 tabular-nums font-medium">
              1 :{' '}
              <input
                autoFocus
                type="text"
                inputMode="numeric"
                value={scaleInputText}
                aria-label="Map scale — type a number and press Enter to jump to it"
                onChange={(event) => setScaleInputText(event.target.value)}
                onFocus={(event) => event.currentTarget.select()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') commitScaleEdit();
                  if (event.key === 'Escape') setEditingScale(false);
                }}
                onBlur={commitScaleEdit}
                className="w-20 rounded border border-blue-300 bg-white px-1 py-0.5 text-right tabular-nums outline-none focus:border-blue-500"
              />
            </span>
          ) : (
            <button
              type="button"
              onClick={startEditingScale}
              className="tabular-nums font-medium underline decoration-dotted underline-offset-2 hover:text-blue-700"
            >
              1 : {Math.round(scaleDenominator).toLocaleString()}
            </button>
          )}
        </div>
      )}
      {createPortal(popupBody, popupContainer)}
    </div>
  );
}
